/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { 
  getAuth, 
  signInAnonymously, 
  onAuthStateChanged,
  signInWithCustomToken,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  onSnapshot, 
  updateDoc, 
  arrayUnion,
  increment,
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  HelpCircle, Heart, Fingerprint, ArrowLeftRight, VenetianMask, Star, Users, Play, ArrowLeft, Crown, Flame, Clock, Send, CheckCircle, XCircle, Monitor, Lock, Unlock, Info, X, RefreshCw, Vote, Search, Bug, Mic, Coins, ShoppingCart, Eye, EyeOff, Skull, ThumbsUp, ThumbsDown, ShieldAlert, TriangleAlert, BookOpen, ChevronDown, User, LogOut, CreditCard, Mail, Loader, Gavel, UserMinus, Volume2, VolumeX, Music
} from 'lucide-react';

import { PALABRAS_IMPOSTOR, CUALIDADES_JUICIO, PREGUNTAS_RULETA } from './diccionario';

// --- AUDIO CONTEXT ---
export const AudioContext = createContext({
  isMuted: false,
  setIsMuted: () => {},
  isMusicMuted: false,
  setIsMusicMuted: () => {},
  playSFX: () => {}
});
export const useAudio = () => useContext(AudioContext);

const MUSIC_TRACKS = {
  lobby: ['music_0_espera_1', 'music_0_espera_2', 'music_0_espera_4'],
  asker: ['music_1_asker_1', 'music_1_asker_2', 'music_1_asker_3'],
  knowme: ['music_2_conocemos_1', 'music_2_conocemos_2', 'music_2_conocemos_3'],
  likely: ['music_3_senalado_1', 'music_3_senalado_2', 'music_3_senalado_3'],
  rather: ['music_4_prefieres_1', 'music_4_prefieres_2', 'music_4_prefieres_3'],
  impostor: ['music_5_impostor_1', 'music_5_impostor_2', 'music_5_impostor_3'],
  secret: ['music_6_secreto_1', 'music_6_secreto_2', 'music_6_secreto_3'],
  declarations: ['music_7_declaraciones_1', 'music_7_declaraciones_2', 'music_7_declaraciones_3'],
  russian: ['music_8_ruleta_1', 'music_8_ruleta_2', 'music_8_ruleta_3'],
  judgment: ['music_9_juicio_1', 'music_9_juicio_2', 'music_9_juicio_3']
};

const getRandomTrack = (tracks) => tracks[Math.floor(Math.random() * tracks.length)];

// --- 1. CONFIGURACIÓN E INICIALIZACIÓN ---

const localFirebaseConfig = {
  apiKey: "AIzaSyBNCsrX6JODSVrqy4st-V5lstyVT4j3_Yc", 
  authDomain: "asker-2b47b.firebaseapp.com",
  projectId: "asker-2b47b",
  storageBucket: "asker-2b47b.firebasestorage.app",
  messagingSenderId: "258092963398",
  appId: "1:258092963398:web:c97bbc6c06a7d0c6635c47",
  measurementId: "G-2DRHSJ7ZRD"
};

const LEMON_SQUEEZY_STORE_ID = "303520";
const VARIANT_ID_MONTHLY = "1367779";
const VARIANT_ID_ANNUAL = "1367780";

let firebaseConfig;
try {
  firebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : localFirebaseConfig;
} catch (e) {
  firebaseConfig = localFirebaseConfig;
}

const app = initializeApp(firebaseConfig);

if (typeof window !== 'undefined') {
    initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider('6Ldn24AsAAAAAKApVx2oeB346LKjXkjzIpPLd_CY'),
        isTokenAutoRefreshEnabled: true
    });
}

const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1'); 
const defaultAppId = 'asker-games-production';
const appId = typeof __app_id !== 'undefined' ? __app_id : defaultAppId;

// --- 2. UTILIDADES ---

const generatePin = () => Math.floor(1000 + Math.random() * 9000).toString();
const generateUUID = () => crypto.randomUUID();
const AVATARS = ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐙', '🦄', '🐝', '🐞', '🦋', '🐢', '🦖', '👻', '👽', '🤖', '💩', '🤡', '👹', '👺', '💀', '🎃', '👾', '🤠', '😎', '🤓', '🥳'];
const getRandomAvatar = () => AVATARS[Math.floor(Math.random() * AVATARS.length)];



const callGemini = async (prompt) => {
  try {
    const generateContent = httpsCallable(functions, 'generateGameContent');
    const result = await generateContent({ prompt });
    return result.data;
  } catch (error) {
    console.error("Error llamando a Gemini Cloud Function:", error);
    alert("Hay mucha gente jugando y los servidores están saturados. Inténtalo más tarde.");
    return null;
  }
};

// --- 3. DEFINICIÓN DE JUEGOS ---

const GAMES = [
  { 
      id: 'asker', 
      title: 'Asker', 
      description: 'Demuestra quién sabe más sobre el tema que queráis. Responde rápido para ganar más puntos. ¡Las rachas de aciertos multiplican!', 
      summary: 'El anfitrión escribe un tema y una dificultad para que el juego cree preguntas. Ganará la persona que acierte más veces, en el menor tiempo y con la mayor racha de aciertos.', 
      icon: <HelpCircle size={40}/>, 
      capacity: '20 max', capacityMin: 1, capacityMax: 20, timer: 20,
      topics: ["Cultura general", "Noticias de actualidad", "Historia", "Música", "Deportes", "Religión", "Geografía", "Ciencia", "Literatura", "Política", "Cine y series", "Videojuegos", "Cuerpo humano", "Celebridades", "Tecnología", "Animales", "Records mundiales", "Estadísticas y cifras", "Gastronomía", "Cultura de internet"]
  },
  { 
      id: 'knowme', 
      title: '¿Nos Conocemos?', 
      description: 'El juego preguntará sobre las preferencias de cada jugador por turnos. ¿Quién conoce mejor a los demás?', 
      summary: 'El juego preguntará por turnos sobre los gustos de cada jugador. La respuesta correcta la decide el jugador sobre el que se pregunta. Este solo gana puntos si alguien acierta (para que nadie conteste mal a propósito). Para los demás acertar suma 1 punto y fallar resta 1 punto. Gana la persona que mejor conozca a los demás.', 
      icon: <Heart size={40}/>, 
      capacity: '20 max', capacityMin: 3, capacityMax: 20, timer: 30,
      topics: ["Random", "Entretenimiento", "Hábitos", "Comida", "Amor", "Amistad", "Regalos", "Viajes y aventuras", "Antojos", "Ambiciones", "Manías", "Supervivencia", "Gustos culposos"]
  },
  { 
      id: 'likely', 
      title: 'Señalado', 
      description: '¿Serás el señalado? ¿Quien es más probable que…?', 
      summary: 'El juego pregunta ¿quien es mas probable que…? Y los jugadores votan. No hay ganadores ni perdedores, solo verdades.', 
      icon: <Fingerprint size={40}/>, 
      capacity: '20 max', capacityMin: 3, capacityMax: 20, timer: 20,
      topics: ["Situaciones vergonzosas", "Liarla parda", "Redes sociales", "Caprichos", "Frikismo", "Supervivencia", "Dilemas de convivencia", "Modales", "Outfit", "Redflags", "Amor", "Familia", "Amigos", "Mundo laboral"]
  },
  { 
      id: 'rather', 
      title: 'Qué Prefieres', 
      description: 'Elige entre dos opciones controvertidas. Descubre si piensas como la mayoría o eres especial.', 
      summary: 'Elige entre dos opciones controvertidas. Descubre si piensas como la mayoría o eres especial.', 
      icon: <ArrowLeftRight size={40}/>, 
      capacity: '30 max', capacityMin: 1, capacityMax: 30, timer: 40,
      topics: ["Decisiones difíciles", "Superpoderes inútiles", "Comidas asquerosas", "Privacidad", "Viajes en el tiempo", "Modificaciones corporales", "Dilemas sociales", "Escenarios apocalípticos", "Amistades", "Redflags", "Trabajos raros"]
  },
  { 
      id: 'impostor', 
      title: 'El Impostor', 
      description: 'Todos tienen la palabra secreta menos el impostor. ¡Descubre quién es!', 
      summary: 'Cada jugador escoge una palabra clave. Por rondas, el juego da la misma palabra a todos los jugadores menos a 1 (el impostor). Por turnos los jugadores pondrán una palabra para demostrar que no son el impostor, pero no demasiado obvia para que el impostor no adivine. Después podréis votar quién es el impostor.', 
      icon: <VenetianMask size={40}/>, 
      capacity: '15 max', capacityMin: 3, capacityMax: 15, timer: 0 
  }, 
  { 
      id: 'secret', 
      title: 'El Secreto', 
      description: 'Todos escribís un secreto personal. Si la mayoría descubre de quién es cada secreto, ¡se revela el autor!', 
      summary: 'Todos los jugadores escriben un secreto personal (en vosotros queda decidir cuanto de secreto). Después se irá votando por turnos aleatorios a quien creéis que pertenece cada secreto, y si la mayoría vota bien, se revelará al autor, sino quedará oculto para siempre.', 
      icon: <Star size={40}/>, 
      capacity: '30 max', capacityMin: 3, capacityMax: 30, timer: 0 
  }, 
  { 
      id: 'declarations', 
      title: 'Duras Declaraciones', 
      description: 'Alguien escribe una frase. Los demás solo veis la mitad de las letras. Pero puedes comprar con puntos letras aleatorias e intentar adivinarla antes que nadie. ¡Cuidado, solo tienes un intento!', 
      summary: 'Un jugador aleatorio escribe la frase que quiera (máximo 50 letras). Al resto solo les aparecerá la mitad de las letras (pueden comprar letras aleatorias con sus puntos), y tendrán que adivinar la frase en 1 solo intento. El ganador se lleva 1000 puntos cada ronda.', 
      icon: <Mic size={40}/>, 
      capacity: '30 max', capacityMin: 4, capacityMax: 30, timer: 60 
  },
  { 
      id: 'russian', 
      title: 'Ruleta Rusa', 
      description: 'Dispara una pregunta anónima al jugador que te toque. Contesta la pregunta que te hagan, o resta puntos para negarte.', 
      summary: 'Escribe una pregunta anónima a la persona que te toque (Puedes pulsar el botón de Random si no se te ocurre ninguna pregunta). Después contesta a la pregunta anónima que te hagan.', 
      icon: <Skull size={40}/>, 
      capacity: '30 max', capacityMin: 4, capacityMax: 30, timer: 0 
  },
  { 
      id: 'judgment', 
      title: 'Juicio Final', 
      description: 'Todos os someteréis al juicio del resto. Nadie está preparado para esto.',
      warning: 'Probabilidad de romper amistades 80%', 
      summary: 'El juego os preguntará por turnos para que evaluéis del 1 al 10 una cualidad (buena o mala) de un jugador al azar. Una vez que todos voten, se revelará la nota media.', 
      icon: <Gavel size={40}/>, 
      capacity: '30 max', capacityMin: 3, capacityMax: 30, timer: 0 
  }
];

// --- FONDOS DE JUEGOS ESPECÍFICOS ---

const AskerBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      let frame;
      const stars = Array.from({length: 100}).map(() => ({
         x: Math.random() * window.innerWidth,
         y: Math.random() * window.innerHeight,
         r: 0.5 + Math.random() * 2,
         offset: Math.random() * Math.PI * 2
      }));
      const render = (time) => {
          canvas.width = window.innerWidth; canvas.height = window.innerHeight;
          const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
          grad.addColorStop(0, '#0B0B2A'); 
          grad.addColorStop(1, '#1B0B3B');
          ctx.fillStyle = grad; 
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          stars.forEach(s => {
              const alpha = 0.2 + (Math.sin(time * 0.002 + s.offset) + 1) * 0.4;
              ctx.fillStyle = `rgba(255,255,255,${alpha})`;
              ctx.shadowBlur = s.r * 3;
              ctx.shadowColor = 'white';
              ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI*2); ctx.fill();
          });
          frame = requestAnimationFrame(render);
      };
      render(0);
      return () => cancelAnimationFrame(frame);
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 z-[-1] pointer-events-none" />;
};

const KnowMeBackground = () => (
   <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none" style={{ background: 'radial-gradient(circle at center, #007FFF 0%, #64B5F6 50%, #FF4081 100%)' }}>
       {Array.from({length: 10}).map((_, i) => (
           <div key={i} className="absolute rounded-full bg-white mix-blend-overlay" style={{
               width: `${100 + Math.random() * 200}px`,
               height: `${100 + Math.random() * 200}px`,
               top: `${Math.random() * 100}%`,
               left: `${Math.random() * 100}%`,
               opacity: 0.1 + Math.random() * 0.3
           }} />
       ))}
   </div>
);

const LikelyBackground = () => (
   <div className="fixed inset-0 z-[-1] bg-[#4fc3f7] overflow-hidden pointer-events-none">
       {/* Nubes compuestas por círculos matemáticos */}
       <svg className="absolute top-[10%] left-[10%] opacity-80 w-48 h-24 text-white" fill="currentColor" viewBox="0 0 100 50">
           <circle cx="30" cy="30" r="15" />
           <circle cx="50" cy="20" r="20" />
           <circle cx="70" cy="30" r="15" />
           <rect x="30" y="20" width="40" height="25" rx="5" />
       </svg>
       <svg className="absolute top-[30%] left-[60%] opacity-75 w-64 h-32 text-white" fill="currentColor" viewBox="0 0 100 50">
           <circle cx="25" cy="30" r="15" />
           <circle cx="50" cy="25" r="20" />
           <circle cx="75" cy="30" r="15" />
           <rect x="25" y="25" width="50" height="20" rx="5" />
       </svg>
       <svg className="absolute top-[60%] left-[20%] opacity-60 w-56 h-28 text-white" fill="currentColor" viewBox="0 0 100 50">
           <circle cx="30" cy="35" r="15" />
           <circle cx="50" cy="25" r="20" />
           <circle cx="70" cy="35" r="15" />
           <rect x="30" y="25" width="40" height="25" rx="5" />
       </svg>
       <svg className="absolute top-[75%] left-[75%] opacity-85 w-40 h-20 text-white" fill="currentColor" viewBox="0 0 100 50">
           <circle cx="30" cy="30" r="15" />
           <circle cx="50" cy="20" r="20" />
           <circle cx="70" cy="30" r="15" />
           <rect x="30" y="20" width="40" height="25" rx="5" />
       </svg>
   </div>
);

const RatherBackground = () => (
   <div className="fixed inset-0 z-[-1] pointer-events-none" style={{ background: 'radial-gradient(circle, #bbf7d0 0%, #fde047 100%)' }} />
);

const ImpostorBackground = () => (
   <div className="fixed inset-0 z-[-1] bg-gray-950 overflow-hidden pointer-events-none">
       <div className="absolute top-[10%] left-[10%] w-[40vw] h-[40vw] bg-purple-600/40 rounded-full mix-blend-screen blur-[80px] animate-[pulsate_4s_infinite_alternate]" />
       <div className="absolute top-[40%] right-[10%] w-[50vw] h-[50vw] bg-pink-600/30 rounded-full mix-blend-screen blur-[100px] animate-[pulsate_5s_infinite_alternate_reverse]" />
       <div className="absolute bottom-[10%] left-[30%] w-[35vw] h-[35vw] bg-cyan-600/40 rounded-full mix-blend-screen blur-[90px] animate-[pulsate_3s_infinite_alternate]" />
   </div>
);

const SecretBackground = () => (
   <div className="fixed inset-0 z-[-1] pointer-events-none" style={{ background: 'radial-gradient(circle, #10b981 0%, #064e3b 100%)' }}>
       <div className="absolute top-0 left-0 w-full h-full bg-white mix-blend-overlay opacity-20" style={{ clipPath: 'polygon(40% 0, 60% 0, 100% 100%, 0% 100%)' }} />
   </div>
);

const DeclarationsBackground = () => (
   <div className="fixed inset-0 z-[-1] bg-gradient-to-b from-purple-700 to-purple-900 grid grid-cols-5 grid-rows-2 gap-4 p-8 overflow-hidden pointer-events-none">
       {Array.from({length: 10}).map((_, i) => (
           <div key={i} className="w-full h-full bg-white/10" style={{
               clipPath: i % 2 === 0 ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 'polygon(20% 20%, 80% 20%, 80% 80%, 20% 80%)',
               transform: `rotate(${Math.random() * 45}deg) scale(${0.5 + Math.random()*0.5})`
           }} />
       ))}
   </div>
);

const RussianBackground = () => (
   <div className="fixed inset-0 z-[-1] bg-gradient-to-b from-gray-900 via-gray-800 to-black pointer-events-none" />
);

const JudgmentBackground = () => {
   const colors = ['#E8A359', '#DE9B52', '#CD853F', '#A0522D', '#8B4513', '#5C3317'];
   return (
       <div className="fixed inset-0 z-[-1] overflow-hidden flex items-center justify-center bg-[#5C3317] pointer-events-none">
           {colors.map((color, i) => (
               <div key={i} className="absolute rounded-full" style={{
                   backgroundColor: color,
                   width: `${(6 - i) * 25}vmin`,
                   height: `${(6 - i) * 25}vmin`,
                   zIndex: i
               }} />
           ))}
       </div>
   );
};

const ResultsBackground = () => (
   <div className="fixed inset-0 z-[-1] flex flex-col overflow-hidden pointer-events-none">
       <div className="flex-1 bg-[#87CEEB] relative">
           <div className="absolute top-10 right-10 w-24 h-24 bg-yellow-300 rounded-full shadow-[0_0_40px_rgba(253,224,71,0.8)]" />
           {/* Modificado: top-[5%] para subirla y w-28 h-14 para hacerla más pequeña */}
           <div className="absolute top-[5%] left-[-20%] animate-[moveRight_30s_linear_infinite]">
               <svg className="w-28 h-14 text-white/90" fill="currentColor" viewBox="0 0 100 50">
                   <circle cx="30" cy="30" r="15" />
                   <circle cx="50" cy="20" r="20" />
                   <circle cx="70" cy="30" r="15" />
                   <rect x="30" y="20" width="40" height="25" rx="5" />
               </svg>
           </div>
       </div>
       <div className="h-[40%] bg-[#4CAF50] relative">
           {/* Los círculos superpuestos han sido eliminados */}
       </div>
   </div>
);

const GameBackgrounds = ({ gameId }) => {
    if (gameId === 'asker') return <AskerBackground />;
    if (gameId === 'knowme') return <KnowMeBackground />;
    if (gameId === 'likely') return <LikelyBackground />;
    if (gameId === 'rather') return <RatherBackground />;
    if (gameId === 'impostor') return <ImpostorBackground />;
    if (gameId === 'secret') return <SecretBackground />;
    if (gameId === 'declarations') return <DeclarationsBackground />;
    if (gameId === 'russian') return <RussianBackground />;
    if (gameId === 'judgment') return <JudgmentBackground />;
    return null;
};

// --- 4. COMPONENTES UI Y COMPARTIDOS ---

const AnimatedBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);
    resize();

    const particleColors = [
      'rgba(0, 255, 255, 1)',
      'rgba(255, 20, 147, 1)',
      'rgba(50, 205, 50, 1)'
    ];

    const particles = Array.from({ length: 35 }).map(() => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      radius: Math.random() * 2 + 1.5,
      color: particleColors[Math.floor(Math.random() * particleColors.length)]
    }));

    const render = () => {
      time += 0.01;
      ctx.fillStyle = 'rgba(10, 5, 30, 1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = 'rgba(30, 10, 60, 0.4)';
      ctx.beginPath();
      ctx.moveTo(0, canvas.height);
      for (let i = 0; i <= canvas.width; i += 20) {
        const y = canvas.height * 0.6 + Math.sin(i * 0.004 + time) * 60 + Math.cos(i * 0.002 - time) * 40;
        ctx.lineTo(i, y);
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.fill();

      ctx.fillStyle = 'rgba(25, 8, 50, 0.6)';
      ctx.beginPath();
      ctx.moveTo(0, canvas.height);
      for (let i = 0; i <= canvas.width; i += 20) {
        const y = canvas.height * 0.8 + Math.sin(i * 0.006 - time * 1.5) * 40 + Math.cos(i * 0.003 + time) * 50;
        ctx.lineTo(i, y);
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.fill();

      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.shadowBlur = 15;
        ctx.shadowColor = p.color;
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 z-[-1] pointer-events-none" />;
};


const isPlayerPending = (uid, room) => {
    if (!room) return false;
    
    if (room.roundState === 'active') {
        if (['asker', 'knowme', 'likely', 'rather'].includes(room.gameId)) {
            return !room.answers?.[uid];
        }
        if (room.gameId === 'impostor') {
            if (room.phase === 'input') {
                const currentWriterId = room.impostorTurnOrder?.[(room.currentTurnIdx || 0) % (room.impostorTurnOrder?.length || 1)];
                return uid === currentWriterId;
            }
            if (room.phase === 'voting') {
                if (uid === room.impostorId) return false;
                return !room.votes?.[uid];
            }
        }
        if (room.gameId === 'secret') {
            if (room.phase === 'input') return !room.secrets?.[uid];
            if (room.phase === 'revealing') return !room.secretVotes?.[room.currentSecretIdx]?.[uid];
        }
        if (room.gameId === 'declarations') {
            if (room.phase === 'writing') {
                const writerId = room.turnOrder?.[room.currentWriterIdx || 0];
                return uid === writerId;
            }
            if (room.phase === 'guessing') {
                const writerId = room.turnOrder?.[room.currentWriterIdx || 0];
                if (uid === writerId) return false;
                return !room.attempts?.[uid];
            }
        }
        if (room.gameId === 'russian') {
            if (room.phase === 'write') return !(room.r_questions || []).some(q => q.askerId === uid);
            if (room.phase === 'answer') {
                const pendingQ = (room.r_questions || []).find(q => q.targetId === uid && q.answer === null && !q.skipped);
                return !!pendingQ;
            }
            if (room.phase === 'voting') {
                const currentVoteQ = (room.r_questions || []).filter(q => !q.skipped)[room.currentVoteIdx];
                if (!currentVoteQ || currentVoteQ.targetId === uid) return false;
                return !currentVoteQ.votes?.[uid];
            }
        }
        if (room.gameId === 'judgment') {
            if (room.phase === 'voting') {
                const targetId = room.judgmentRounds?.[room.currentJudgmentIdx || 0]?.targetId;
                if (uid === targetId) return false;
                return room.j_votes?.[uid] === undefined;
            }
        }
    }
    return false;
};


const Button = ({ onClick, children, className = "", disabled = false, variant = "primary" }) => {
  let btnClasses = "px-6 py-3 rounded-xl uppercase tracking-widest flex items-center justify-center gap-2 transition-all w-full ";
  
  if (variant === "primary") {
      btnClasses += "btn-neon-cyan text-black font-black ";
  } else if (variant === "danger") {
      btnClasses += "bg-[rgba(255,20,147,0.8)] text-white shadow-[0_0_15px_rgba(255,20,147,0.8)] font-black hover:bg-[rgba(255,20,147,1)] ";
  } else if (variant === "success") {
      btnClasses += "bg-[rgba(50,205,50,0.8)] text-white shadow-[0_0_15px_rgba(50,205,50,0.8)] font-black hover:bg-[rgba(50,205,50,1)] ";
  } else if (variant === "outline" || variant === "secondary") {
      btnClasses += "cyber-card hover:bg-white/10 text-white font-bold ";
  } else if (variant === "neon" || variant === "yellow") {
      btnClasses += "btn-neon-cyan text-black font-black ";
  } else {
      btnClasses += "cyber-card hover:bg-white/10 text-white font-bold ";
  }

  return (
    <button 
      onClick={onClick} 
      disabled={disabled} 
      className={`${btnClasses} ${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
};

const Input = ({ value, onChange, placeholder, className = "", type = "text", maxLength }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    maxLength={maxLength}
    className={`cyber-input px-4 py-3 rounded-xl w-full ${className}`}
  />
);

const Card = ({ children, className = "", color = "violet" }) => {
  return (
    <div className={`cyber-card p-6 md:p-8 ${className}`}>
      {children}
    </div>
  );
};

const KickModal = ({ room, onClose, onKick }) => {
    const [selected, setSelected] = useState([]);
    const activePlayers = room.players.filter(p => !p.isSpectator && !p.hasLeft && !p.isHost);
    
    const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleKick = () => {
        if (selected.length === 0) return;
        onKick(selected);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
            <div className="cyber-card p-6 rounded-2xl max-w-md w-full relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={20}/></button>
                <h3 className="text-xl font-bold text-red-500 mb-4 flex items-center gap-2"><UserMinus size={20}/> Expulsar Jugadores</h3>
                <p className="text-sm text-[rgba(255,255,255,0.6)] mb-4">Selecciona a quién quieres expulsar. Los jugadores en <span className="text-red-400 font-bold">rojo</span> tienen una acción pendiente.</p>
                
                <div className="max-h-64 overflow-y-auto space-y-2 mb-6 pr-2">
                    {activePlayers.map(p => {
                        const isPending = isPlayerPending(p.id, room);
                        const isSelected = selected.includes(p.id);
                        return (
                            <div 
                                key={p.id} 
                                onClick={() => toggle(p.id)}
                                className={`p-3 rounded-lg border-2 cursor-pointer flex justify-between items-center transition-all ${isSelected ? 'bg-red-900/50 border-red-500' : 'bg-transparent border-[rgba(255,255,255,0.1)] hover:border-fuchsia-500'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{p.avatar}</span>
                                    <span className={`font-bold ${isPending ? 'text-red-400' : 'text-white'}`}>{p.nickname}</span>
                                </div>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'bg-red-500 border-red-500' : 'border-gray-500'}`}>
                                    {isSelected && <CheckCircle size={14} className="text-white"/>}
                                </div>
                            </div>
                        )
                    })}
                    {activePlayers.length === 0 && <p className="text-[rgba(255,255,255,0.6)] text-center py-4">No hay jugadores para expulsar.</p>}
                </div>

                <Button onClick={handleKick} variant="danger" className="w-full !shadow-none !border-none !bg-red-600 !text-white" disabled={selected.length === 0}>
                    Confirmar Expulsión ({selected.length})
                </Button>
            </div>
        </div>
    );
};


const AuthModal = ({ onClose, onLoginSuccess }) => {
    const [isRegister, setIsRegister] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [highlightTerms, setHighlightTerms] = useState(false);

    const handleResetPassword = async () => {
        if (!email) {
            return setError("Por favor, escribe tu email arriba para recuperar la contraseña.");
        }
        setError("");
        try {
            await sendPasswordResetEmail(auth, email);
            alert(`Se ha enviado un enlace de recuperación a ${email}. Si el problema persiste contactanos en contactoaskergames@gmail.com`);
        } catch (e) {
            if (e.code === 'auth/user-not-found') {
                setError("Correo no registrado.");
            } else {
                setError(e.message);
            }
        }
    };

    const handleGoogle = async () => {
        if (!acceptedTerms) {
            setHighlightTerms(true);
            return setError("Debes aceptar las condiciones para continuar.");
        }
        setHighlightTerms(false);
        setLoading(true);
        setError("");
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            onLoginSuccess();
            onClose();
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    };

    const handleEmail = async () => {
        if (!acceptedTerms) {
            setHighlightTerms(true);
            return setError("Debes aceptar las condiciones para continuar.");
        }
        if (!email || !password) return setError("Rellena todos los campos");
        setHighlightTerms(false);
        setLoading(true);
        setError("");
        try {
            if (isRegister) {
                await createUserWithEmailAndPassword(auth, email, password);
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            onLoginSuccess();
            onClose();
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
            <div className="cyber-card p-8 max-w-sm w-full relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={24}/></button>
                <h2 className="text-2xl font-black text-white mb-4 text-center">{isRegister ? "Crear Cuenta" : "Iniciar Sesión"}</h2>

                <div 
                    className="mb-6 text-center text-sm font-bold p-3 rounded-xl cursor-pointer transition-all bg-[rgba(255,20,147,0.2)] text-[rgba(255,20,147,1)] border border-[rgba(255,20,147,0.5)] shadow-[0_0_15px_rgba(255,20,147,0.3)] hover:scale-105"
                    onClick={() => { setIsRegister(!isRegister); setError(""); }}
                >
                    {isRegister ? "¿Ya tienes cuenta? Entra aquí" : "¿No tienes cuenta? Regístrate"}
                </div>

                <div className={`flex items-start gap-3 mb-4 bg-black/40 p-3 rounded-lg border transition-all duration-300 ${highlightTerms ? 'border-[rgba(255,20,147,1)] shadow-[0_0_15px_rgba(255,20,147,0.6)]' : 'border-white/10'}`}>
                    <input 
                        type="checkbox" 
                        id="terms" 
                        checked={acceptedTerms} 
                        onChange={(e) => { setAcceptedTerms(e.target.checked); setHighlightTerms(false); }}
                        className="mt-1 w-4 h-4 rounded border-[rgba(0,255,255,0.5)] bg-transparent focus:ring-[rgba(0,255,255,1)] cursor-pointer"
                    />
                    <label htmlFor="terms" className="text-xs text-[rgba(255,255,255,0.6)] leading-relaxed cursor-pointer select-none">
                        He leído y acepto las <a href="/terminos-condiciones.html" target="_blank" rel="noopener noreferrer" className="text-[rgba(0,255,255,1)] hover:text-white underline" onClick={e => e.stopPropagation()}>condiciones de contratación</a> y la <a href="/politica-privacidad.html" target="_blank" rel="noopener noreferrer" className="text-[rgba(0,255,255,1)] hover:text-white underline" onClick={e => e.stopPropagation()}>política de privacidad</a>.
                    </label>
                </div>
                
                <Button onClick={handleGoogle} variant="outline" className="w-full mb-4">
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5 mr-2" alt="Google"/>
                    Continuar con Google
                </Button>

                <div className="flex items-center gap-2 mb-4">
                    <div className="h-px bg-[rgba(255,255,255,0.2)] flex-1"/>
                    <span className="text-xs text-[rgba(255,255,255,0.6)]">O con email</span>
                    <div className="h-px bg-[rgba(255,255,255,0.2)] flex-1"/>
                </div>

                <div className="space-y-3 mb-2">
                    <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" maxLength={50} />
                    <Input value={password} onChange={e => setPassword(e.target.value)} placeholder="Contraseña" type="password" maxLength={50} />
                </div>

                <div className="text-right mb-6">
                    <button onClick={handleResetPassword} className="text-xs text-[rgba(0,255,255,1)] hover:text-white underline cursor-pointer bg-transparent border-none outline-none">
                        ¿Has olvidado tu contraseña?
                    </button>
                </div>

                {error && <p className="text-[rgba(255,20,147,1)] text-xs mb-4 text-center font-bold">{error}</p>}

                <Button onClick={handleEmail} variant={isRegister ? "success" : "primary"} className="w-full mb-4" disabled={loading}>
                    {loading ? "Cargando..." : (isRegister ? "Registrarse" : "Entrar")}
                </Button>
            </div>
        </div>
    );
};

const SubscriptionModal = ({ onClose }) => {
    const [loading, setLoading] = useState(false);

    const handleCheckout = async (variantId) => {
        setLoading(true);
        try {
            let detectedCountry = "ES";
            let detectedZip = "";
            try {
                const navCountry = navigator.language.split('-')[1];
                if (navCountry) detectedCountry = navCountry.toUpperCase();
                const ipRes = await fetch('https://ipapi.co/json/');
                if (ipRes.ok) {
                    const ipData = await ipRes.json();
                    if (ipData.country_code) detectedCountry = ipData.country_code;
                    if (ipData.postal) detectedZip = ipData.postal;
                }
            } catch (e) {
                console.warn("No se pudo detectar la ubicación exacta, usando país por defecto.");
            }

            const createCheckout = httpsCallable(functions, 'createLemonSqueezyCheckout');
            const result = await createCheckout({
                variantId,
                storeId: LEMON_SQUEEZY_STORE_ID,
                redirectUrl: window.location.origin + '/?payment=success',
                userCountry: detectedCountry,
                userZip: detectedZip
            });
            
            if (result.data.url) {
                window.location.href = result.data.url;
            } else {
                throw new Error("No URL returned");
            }
        } catch (e) {
            console.error("Error al iniciar pago:", e);
            window.alert("Error iniciando pago. Asegúrate de haber iniciado sesión.");
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in overflow-y-auto pointer-events-auto">
            <div className="cyber-card p-6 md:p-10 max-w-4xl w-full relative">
                <button 
                    onClick={() => onClose({ success: false })} 
                    className="absolute top-4 right-4 text-white/50 hover:text-white transition-all p-2 z-[110]"
                >
                    <X size={24} className="pointer-events-none" />
                </button>
                
                <div className="text-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[rgba(0,255,255,1)] to-[rgba(255,20,147,1)] mb-2 drop-shadow-[0_0_10px_rgba(255,20,147,0.5)]">
                        Desbloquea partidas ilimitadas
                    </h2>
                    <p className="text-[rgba(255,255,255,0.6)] text-lg">Juega con todos tus amigos</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-black/40 rounded-2xl p-6 border border-white/10 hover:border-[rgba(0,255,255,0.5)] transition-all flex flex-col shadow-[0_0_15px_rgba(0,0,0,0.5)]">
                        <h3 className="text-xl font-bold text-white mb-2">Mensual</h3>
                        <div className="text-3xl font-black text-white mb-4">3,99€ <span className="text-sm font-normal text-[rgba(255,255,255,0.6)]">/mes</span></div>
                        <ul className="space-y-3 mb-8 flex-1">
                            <li className="flex items-center gap-2 text-[rgba(255,255,255,0.8)]"><CheckCircle size={16} className="text-[rgba(50,205,50,1)]"/> Paga 1, juegan todos</li>
                            <li className="flex items-center gap-2 text-[rgba(255,255,255,0.8)]"><CheckCircle size={16} className="text-[rgba(50,205,50,1)]"/> Salas hasta 30 jugadores</li>
                            <li className="flex items-center gap-2 text-[rgba(255,255,255,0.8)]"><CheckCircle size={16} className="text-[rgba(50,205,50,1)]"/> Desbloquea más preguntas por partida</li>
                        </ul>
                        <Button onClick={() => handleCheckout(VARIANT_ID_MONTHLY)} variant="outline" className="w-full" disabled={loading}>
                            {loading ? <Loader className="animate-spin"/> : "Elegir Mensual"}
                        </Button>
                    </div>

                    <div className="bg-[rgba(255,20,147,0.2)] rounded-2xl p-6 border-2 border-[rgba(255,20,147,1)] shadow-[0_0_20px_rgba(255,20,147,0.8)] relative flex flex-col">
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[rgba(255,20,147,1)] text-white text-xs font-black px-3 py-1 rounded-full shadow-[0_0_10px_rgba(255,20,147,1)] tracking-widest">MEJOR VALOR</div>
                        <h3 className="text-xl font-bold text-white mb-2">Anual</h3>
                        <div className="flex items-end gap-2 mb-4">
                            <div className="text-3xl font-black text-white">9,99€ <span className="text-sm font-normal text-[rgba(255,255,255,0.8)]">/año</span></div>
                            <div className="text-3xl text-[rgba(255,255,255,0.4)] line-through mb-1 decoration-[rgba(255,20,147,1)] decoration-2">47,88€</div>
                        </div>
                        <ul className="space-y-3 mb-8 flex-1">
                            <li className="flex items-center gap-2 text-white font-bold"><CheckCircle size={16} className="text-[rgba(0,255,255,1)]"/> Todo lo de mensual</li>
                            <li className="flex items-center gap-2 text-white"><CheckCircle size={16} className="text-[rgba(0,255,255,1)]"/> Prioridad en soporte</li>
                            <li className="flex items-center gap-2 text-white"><CheckCircle size={16} className="text-[rgba(0,255,255,1)]"/> Ahorras un 80%</li>
                        </ul>
                        <Button onClick={() => handleCheckout(VARIANT_ID_ANNUAL)} variant="primary" className="w-full" disabled={loading}>
                            {loading ? <Loader className="animate-spin"/> : "Elegir Anual"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const GameCard = ({ game, onClick }) => {
  const gameColors = {
      asker: { accent: 'rgba(0, 255, 255, 1)', alpha: 'rgba(0, 255, 255, 0.2)' },
      knowme: { accent: 'rgba(255, 20, 147, 1)', alpha: 'rgba(255, 20, 147, 0.2)' },
      likely: { accent: 'rgba(255, 215, 0, 1)', alpha: 'rgba(255, 215, 0, 0.2)' },
      rather: { accent: 'rgba(255, 69, 0, 1)', alpha: 'rgba(255, 69, 0, 0.2)' },
      impostor: { accent: 'rgba(220, 20, 60, 1)', alpha: 'rgba(220, 20, 60, 0.2)' },
      secret: { accent: 'rgba(50, 205, 50, 1)', alpha: 'rgba(50, 205, 50, 0.2)' },
      declarations: { accent: 'rgba(138, 43, 226, 1)', alpha: 'rgba(138, 43, 226, 0.2)' },
      russian: { accent: 'rgba(255, 255, 255, 0.8)', alpha: 'rgba(255, 255, 255, 0.2)' },
      judgment: { accent: 'rgba(255, 140, 0, 1)', alpha: 'rgba(255, 140, 0, 0.2)' }
  };
  
  const styling = gameColors[game.id] || { accent: 'rgba(0, 255, 255, 1)', alpha: 'rgba(0, 255, 255, 0.2)' };

  return (
    <div className="relative group p-2">
        <div 
        onClick={() => onClick(game)}
        className="game-card-cyber relative h-64 rounded-2xl p-6 cursor-pointer overflow-hidden flex flex-col justify-between"
        style={{
            '--accent': styling.accent,
            '--accent-alpha': styling.alpha
        }}
        >
        <div className="relative z-10 flex flex-col h-full justify-between pointer-events-none">
            <div>
                <div className="mb-4 text-white game-icon transition-all duration-300">
                    {game.icon}
                </div>
                <h3 className="text-2xl font-black text-white uppercase tracking-wider mb-2" style={{textShadow: `0 0 10px ${styling.accent}`}}>{game.title}</h3>
                <p className="text-[rgba(255,255,255,0.8)] text-sm font-medium leading-relaxed line-clamp-3">{game.description}</p>
                {game.warning && (
                  <div className="inline-flex items-center gap-1 bg-black text-[rgba(255,215,0,1)] font-bold text-[10px] sm:text-xs mt-2 px-2 py-1 rounded border border-[rgba(255,215,0,0.5)]">
                    <TriangleAlert size={14} className="text-[rgba(255,215,0,1)]"/>
                    <span className="underline decoration-[rgba(255,215,0,1)]">{game.warning}</span>
                  </div>
                )}
            </div>
            <div className="flex justify-between items-end">
            <span className="text-xs font-bold bg-black/60 px-2 py-1 rounded text-white border border-white/20">
                <Users size={12} className="inline mr-1"/> {game.capacity}
            </span>
            </div>
        </div>
        </div>
    </div>
  );
};

const GameInfoModal = ({ game, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in pointer-events-auto">
            <div className="cyber-card relative w-full max-w-lg z-[110]">
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 text-white/50 hover:text-white transition-all p-2"
                >
                    <X size={24} className="pointer-events-none" />
                </button>
                <div className="text-center mb-6">
                    <div className="text-6xl mb-4 text-[rgba(0,255,255,1)] drop-shadow-[0_0_15px_rgba(0,255,255,0.8)]">{game.icon}</div>
                    <h2 className="text-3xl font-black text-white uppercase tracking-widest mb-2">{game.title}</h2>
                </div>
                <div className="bg-black/40 p-6 rounded-xl border border-[rgba(255,255,255,0.1)] text-left shadow-inner">
                    <h4 className="font-bold text-white mb-2 flex items-center gap-2"><BookOpen size={18} className="text-[rgba(0,255,255,1)]"/> Cómo jugar:</h4>
                    <p className="text-[rgba(255,255,255,0.8)] leading-relaxed text-sm">{game.summary}</p>
                </div>
                <div className="mt-8">
                    <Button onClick={onClose} variant="primary" className="w-full">¡Entendido!</Button>
                </div>
            </div>
        </div>
    );
};

const BugReportModal = ({ onClose, user, appId }) => {
    const [email, setEmail] = useState("");
    const [description, setDescription] = useState("");
    const [sending, setSending] = useState(false);

    const handleSubmit = async () => {
        if (!description.trim()) {
            alert("Por favor, describe el error.");
            return;
        }
        setSending(true);
        try {
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'bug_reports'), {
                description,
                contactEmail: email || "Anonymous",
                userId: user?.uid || "unknown",
                timestamp: Date.now(),
                status: 'new'
            });
            alert("¡Informe enviado! Gracias por ayudarnos a mejorar.");
            onClose();
        } catch (e) {
            console.error("Error enviando reporte:", e);
            alert("Hubo un problema enviando el reporte. Inténtalo de nuevo.");
        }
        setSending(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in pointer-events-auto">
            <div className="cyber-card p-6 md:p-8 !border-[rgba(255,20,147,0.8)] !shadow-[0_0_30px_rgba(255,20,147,0.4)] max-w-md w-full relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white transition-all p-2">
                    <X size={20} className="pointer-events-none" />
                </button>
                
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Bug className="text-[rgba(255,20,147,1)] drop-shadow-[0_0_10px_rgba(255,20,147,0.8)]"/> Errores o Sugerencias
                </h3>
                
                <p className="text-[rgba(255,255,255,0.6)] text-sm mb-4">
                    Incluye tu correo si deseas una respuesta.
                </p>

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-[rgba(255,255,255,0.6)] mb-1 uppercase tracking-widest">Correo (Opcional)</label>
                        <Input 
                            value={email} 
                            onChange={(e) => setEmail(e.target.value)} 
                            placeholder="tu@email.com" 
                            maxLength={50}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-[rgba(255,255,255,0.6)] mb-1 uppercase tracking-widest">Mensaje</label>
                        <textarea 
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Cuéntanos..."
                            maxLength={500}
                            className="cyber-input w-full px-4 py-3 h-32 resize-none rounded-xl"
                        />
                    </div>
                    
                    <Button onClick={handleSubmit} variant="danger" className="w-full" disabled={sending}>
                        {sending ? "Enviando..." : "Enviar Informe"}
                    </Button>
                </div>
            </div>
        </div>
    );
};


const GlobalRanking = ({ room, roomRef, players, onNext, isFinal, gameId, onContinue, onRestart, isHost, roomStatus }) => {
  const { playSFX } = useAudio();
  const activePlayers = players.filter(p => !p.isSpectator);
  const isVotesRanking = gameId === 'likely';
  const scoreKey = isVotesRanking ? 'votesReceivedTotal' : 'score';
  const sortedPlayers = [...activePlayers].sort((a, b) => (b[scoreKey] || 0) - (a[scoreKey] || 0));
  const isTableMode = gameId === 'declarations' || gameId === 'russian';
  const noPodium = gameId === 'rather' || gameId === 'secret' || gameId === 'likely' || gameId === 'impostor' || isTableMode; 

  const hasPlayedEndSound = useRef(false);
  const hasPlayedAbsoluteFinal = useRef(false);
  const [podiumStep, setPodiumStep] = useState(0);

  useEffect(() => {
      if (isFinal && !hasPlayedEndSound.current) {
          hasPlayedEndSound.current = true;
          if (['asker', 'knowme'].includes(gameId)) {
              if (noPodium) playSFX('podio'); // Solo suena directo si no hay animación
          } else if (gameId === 'likely') {
              playSFX('resultados_finales');
          }
      }

      if (isFinal && room?.isFinalResults && !hasPlayedAbsoluteFinal.current && ['declarations', 'russian'].includes(gameId)) {
          hasPlayedAbsoluteFinal.current = true;
          playSFX('resultados_finales');
      }
  }, [isFinal, isHost, gameId, playSFX, room?.isFinalResults, noPodium]);

  // EFECTO DE ANIMACIÓN DEL PODIO Y CONFETI
  useEffect(() => {
      if (isFinal && !noPodium) {
          // Cargamos la librería de confeti
          if (!document.getElementById('confetti-script')) {
              const script = document.createElement('script');
              script.id = 'confetti-script';
              script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
              script.async = true;
              document.body.appendChild(script);
          }

          // Revelación épica con suspense (Tiempos ampliados para mayor tensión)
          const t1 = setTimeout(() => { setPodiumStep(1); playSFX('tubo'); }, 1000); // 3º Puesto
          const t2 = setTimeout(() => { setPodiumStep(2); playSFX('tubo'); }, 2500); // 2º Puesto
          const t3 = setTimeout(() => { 
              setPodiumStep(3); 
              playSFX('podio'); // Sonido Épico del Ganador
              if (window.confetti) {
                  window.confetti({ particleCount: 250, spread: 160, origin: { y: 0.6 }, zIndex: 10000 });
              }
          }, 4500); // 1º Puesto y explosión

          return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
      }
  }, [isFinal, noPodium, playSFX]);

  if (gameId === 'judgment') {
     return (
         <div className="flex flex-col items-center w-full max-w-2xl mx-auto p-4 animate-fade-in">
            <div className="text-center mb-8">
               <Gavel size={64} className="mx-auto text-amber-500 mb-4" />
               <h2 className="text-4xl font-black text-amber-500 neon-text mb-2">¡EL JUICIO HA TERMINADO!</h2>
               <p className="text-gray-400 mt-2">Esperamos que sigáis siendo amigos.</p>
            </div>
            {isHost ? (
               <div className="w-full flex flex-col gap-3">
                   <Button onClick={onRestart} className="bg-green-600 hover:bg-green-500 text-white w-full">Hacer otra ronda</Button>
                   <Button onClick={() => updateDoc(roomRef, { status: 'closed', musicTrack: null })} variant="danger" className="w-full !shadow-none !border-none !bg-red-600 !text-white">Volver al Menú</Button>
               </div>
           ) : (
               <div className="w-full flex flex-col gap-3 mt-4">
                   <Button onClick={onNext} variant="danger" className="w-full !shadow-none !border-none !bg-red-600 !text-white">Volver al Menú</Button>
                   <div className="text-gray-400 animate-pulse text-center mt-2">El anfitrión puede iniciar otra ronda...</div>
               </div>
           )}
         </div>
     )
  }

  if (isTableMode && isFinal) {
      if (!room?.isFinalResults && !isHost) {
          return (
             <div className="flex flex-col items-center w-full max-w-2xl mx-auto p-4 animate-fade-in mt-8">
                <Lock size={64} className="text-gray-500 mb-6 animate-pulse" />
                <h2 className="text-3xl font-black text-white mb-4 text-center neon-text">MANTENIENDO LA EXPECTACIÓN</h2>
                <p className="text-white text-center text-lg">El anfitrión está decidiendo si hacer otra ronda o revelar los resultados finales.</p>
             </div>
          );
      }

      return (
         <div className="flex flex-col items-center w-full max-w-2xl mx-auto p-4 animate-fade-in">
            <h2 className="text-3xl font-black text-white mb-8 neon-text">
                {room?.isFinalResults ? "RESULTADOS FINALES" : "FIN DE LA RONDA"}
            </h2>
            <div className="w-full space-y-2 mb-6">
                {sortedPlayers.map((p, idx) => (
                <div key={p.id} className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                    <span className="text-white font-medium flex items-center gap-3">
                    <span className="text-yellow-500 font-bold">#{idx + 1}</span>
                    {p.nickname}
                    </span>
                    <span className="text-yellow-300 font-bold">{p[scoreKey] || 0} {isVotesRanking ? 'Votos' : 'Créditos'}</span>
                </div>
                ))}
            </div>

            {isHost ? (
                !room?.isFinalResults ? (
                    <div className="w-full flex flex-col gap-3">
                        <Button onClick={onContinue} variant="success" className="w-full">Hacer otra ronda</Button>
                        <Button onClick={() => updateDoc(roomRef, { isFinalResults: true, musicTrack: null })} className="w-full bg-yellow-600 hover:bg-yellow-500 text-white shadow-[0_0_15px_rgba(202,138,4,0.5)]">Terminar partida y ver resultados</Button>
                    </div>
                ) : (
                    <div className="w-full flex flex-col gap-3">
                        <Button onClick={onRestart} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.5)]">Empezar otra partida</Button>
                        <Button onClick={() => { updateDoc(roomRef, { status: 'closed', musicTrack: null }); onNext(); }} variant="danger" className="w-full mt-2 !shadow-none !border-none !bg-red-600 !text-white">Volver al Menú</Button>
                    </div>
                )
            ) : (
                room?.isFinalResults && (
                    <div className="w-full flex flex-col gap-3 mt-4">
                        {room?.status === 'closed' ? (
                            <Button onClick={onNext} variant="danger" className="w-full animate-fade-in !shadow-none !border-none !bg-red-600 !text-white">Volver al Menú</Button>
                        ) : (
                            <div className="text-white animate-pulse text-center text-sm border border-white/10 bg-black/20 p-3 rounded-xl mt-2">
                                Esperando a que el anfitrión termine la partida...
                            </div>
                        )}
                        <p className="text-center text-white text-xs mt-2">Puedes quedarte viendo los resultados el tiempo que quieras.</p>
                    </div>
                )
            )}
         </div>
      );
  }

  if (noPodium) {
     return (
        <div className="flex flex-col items-center w-full max-w-2xl mx-auto p-4 animate-fade-in">
           {gameId === 'likely' && <h2 className="text-3xl font-black text-white mb-8 neon-text">LOS MÁS SEÑALADOS</h2>}
           
           {(gameId === 'likely') && (
               <div className="w-full space-y-2 mb-6">
                {sortedPlayers.map((p, idx) => (
                <div key={p.id} className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                    <span className="text-white font-medium flex items-center gap-3">
                    <span className="text-yellow-500 font-bold">#{idx + 1}</span>
                    {p.nickname}
                    </span>
                    <span className="text-yellow-300 font-bold">{p[scoreKey] || 0} {isVotesRanking ? 'Votos' : 'Créditos'}</span>
                </div>
                ))}
               </div>
           )}
           
           {(gameId === 'rather' || gameId === 'impostor' || gameId === 'secret') && isFinal && (
               <div className="text-center mb-8">
                   <h2 className="text-3xl font-black text-white neon-text">¡PARTIDA FINALIZADA!</h2>
                   <p className="text-white mt-2">Gracias por jugar.</p>
               </div>
           )}
           
           {onNext && <Button onClick={onNext} variant="primary" className="w-full">
            {isFinal ? "Volver al Menú" : "Siguiente"}
          </Button>}
        </div>
     )
  }

  if (!isFinal) {
     return (
        <div className="flex flex-col items-center w-full max-w-2xl mx-auto p-4 animate-fade-in">
           <h2 className="text-3xl font-black text-white mb-8 neon-text">CLASIFICACIÓN</h2>
           <div className="w-full space-y-2 mb-6">
            {sortedPlayers.map((p, idx) => (
              <div key={p.id} className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
                <span className="text-white font-medium flex items-center gap-3">
                  <span className="text-gray-400 text-sm">#{idx + 1}</span>
                  {p.nickname}
                  {p.streak > 3 && <Flame className="text-orange-500 w-4 h-4 animate-pulse" fill="currentColor" />}
                </span>
                <div className="flex items-center gap-3">
                    {p.lastRoundPoints > 0 && (
                        <span className="text-green-400 font-bold text-sm animate-bounce">+{p.lastRoundPoints}</span>
                    )}
                    {p.lastRoundPoints < 0 && (
                        <span className="text-red-500 font-bold text-sm animate-pulse">{p.lastRoundPoints}</span>
                    )}
                    <span className="text-violet-300 font-bold">{p.score || 0} pts</span>
                </div>
              </div>
            ))}
           </div>
           {onNext && <Button onClick={onNext} variant="primary" className="w-full">
            Siguiente Pregunta
          </Button>}
        </div>
     )
  }

  const top3 = sortedPlayers.slice(0, 3);
  const rest = sortedPlayers.slice(3);

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto p-4 animate-fade-in overflow-hidden">
      <h2 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-600 mb-2 drop-shadow-[0_0_15px_rgba(250,204,21,0.5)] uppercase tracking-widest text-center mt-4">
        {podiumStep < 3 ? 'Cargando Podio...' : '¡PODIO FINAL!'}
      </h2>
      {/* SEPARACIÓN AUMENTADA: mb-20 md:mb-28 para alejar el texto del podio */}
      <p className="text-white font-black tracking-widest uppercase mb-20 md:mb-28 h-6 text-sm md:text-base drop-shadow-md">
          {podiumStep === 0 && 'Analizando los resultados...'}
          {podiumStep === 1 && 'El tercer puesto es para...'}
          {podiumStep === 2 && 'Se queda a las puertas...'}
          {podiumStep === 3 && '¡TENEMOS UN CAMPEÓN!'}
      </p>
      
      <div className="flex items-end justify-center gap-2 md:gap-6 mb-16 w-full min-h-[350px] relative">
        
        {/* 2º PUESTO (Plata) */}
        {top3[1] && (
          <div className={`flex flex-col items-center transition-all duration-700 ease-out transform ${podiumStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
            <div className="relative mb-3">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-800/90 rounded-full flex items-center justify-center border-4 border-gray-400 shadow-[0_0_20px_rgba(156,163,175,0.6)] z-10 relative backdrop-blur-md">
                    <span className="text-4xl md:text-5xl animate-jelly">{top3[1].avatar}</span>
                </div>
            </div>
            <div className="text-white font-black text-base md:text-xl mb-1 uppercase tracking-widest drop-shadow-md truncate max-w-[100px] text-center">{top3[1].nickname}</div>
            
            {/* Puntuación negra con recuadro blanco */}
            <div className="bg-white/95 text-black font-black text-2xl md:text-3xl mb-2 px-4 py-1 rounded-xl shadow-md">{top3[1].score || 0} <span className="text-sm md:text-base">pts</span></div>
            
            <div className="podium-bar w-24 md:w-28 bg-gradient-to-t from-gray-900 via-gray-600 to-gray-300 rounded-t-xl border-t-8 border-x-4 border-gray-100 flex items-start justify-center shadow-[0_0_30px_rgba(156,163,175,0.5)] relative overflow-hidden" style={{ height: podiumStep >= 2 ? '9rem' : '0' }}>
              <div className="absolute inset-0 bg-white/10 w-full h-full mix-blend-overlay"></div>
              <span className="text-6xl font-black text-gray-900 mt-4 drop-shadow-md opacity-80">2</span>
            </div>
          </div>
        )}
        
        {/* 1º PUESTO (Oro/Épico) */}
        {top3[0] && (
          <div className={`flex flex-col items-center relative z-20 transition-all duration-1000 ease-out transform ${podiumStep >= 3 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-24 scale-90'}`}>
            
            {/* ESTRELLA GIRATORIA (Detrás de la columna, a su altura y girando en su propio eje) */}
            {podiumStep >= 3 && (
                <div className="absolute top-[60%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 -z-10 pointer-events-none">
                    {/* SVG al 80% (320px) con animate-spin para rotar en su centro perfecto */}
                    <svg width="320" height="320" viewBox="0 0 100 100" className="text-yellow-400 fill-current opacity-70 mix-blend-screen animate-spin" style={{ animationDuration: '15s' }}>
                        <polygon points="50,0 55,40 100,20 60,50 100,80 55,60 50,100 45,60 0,80 40,50 0,20 45,40" />
                    </svg>
                </div>
            )}

            <div className="relative mb-3 flex flex-col items-center">
                {/* Corona perfectamente centrada usando márgenes negativos en vez de transform */}
                {podiumStep >= 3 && (
                    <Crown className="text-yellow-300 w-16 h-16 md:w-20 md:h-20 absolute left-1/2 -ml-8 md:-ml-10 -top-12 md:-top-16 z-30 drop-shadow-[0_0_20px_rgba(250,204,21,1)] animate-crown-drop" />
                )}
                <div className="w-24 h-24 md:w-32 md:h-32 bg-yellow-900/90 rounded-full flex items-center justify-center border-[6px] border-yellow-400 shadow-[0_0_40px_rgba(250,204,21,0.8)] z-10 relative backdrop-blur-md">
                    <span className="text-6xl md:text-7xl animate-jelly" style={{ animationDelay: '4.5s' }}>{top3[0].avatar}</span>
                </div>
            </div>
            
            <div className="text-white font-black text-xl md:text-3xl mb-1 uppercase tracking-widest drop-shadow-lg truncate max-w-[140px] text-center">{top3[0].nickname}</div>
            
            {/* Puntuación negra con recuadro blanco */}
            <div className="bg-white/95 text-black font-black text-3xl md:text-5xl mb-2 px-5 py-1.5 rounded-2xl shadow-lg">{top3[0].score || 0} <span className="text-lg md:text-2xl">pts</span></div>
            
            <div className="podium-bar w-28 md:w-36 bg-gradient-to-t from-yellow-900 via-yellow-600 to-yellow-400 rounded-t-xl border-t-[10px] border-x-4 border-yellow-100 flex items-start justify-center shadow-[0_0_50px_rgba(250,204,21,0.6)] relative overflow-hidden" style={{ height: podiumStep >= 3 ? '13rem' : '0' }}>
              <div className="absolute inset-0 bg-white/20 w-full h-full mix-blend-overlay"></div>
              <span className="text-8xl font-black text-yellow-950 mt-4 drop-shadow-md opacity-90">1</span>
            </div>
          </div>
        )}

        {/* 3º PUESTO (Bronce) */}
        {top3[2] && (
          <div className={`flex flex-col items-center transition-all duration-500 ease-out transform ${podiumStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}>
            <div className="relative mb-3">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-orange-900/90 rounded-full flex items-center justify-center border-4 border-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.6)] z-10 relative backdrop-blur-md">
                    <span className="text-3xl md:text-4xl animate-jelly">{top3[2].avatar}</span>
                </div>
            </div>
            <div className="text-white font-black text-sm md:text-lg mb-1 uppercase tracking-widest drop-shadow-md truncate max-w-[90px] text-center">{top3[2].nickname}</div>
            
            {/* Puntuación negra con recuadro blanco */}
            <div className="bg-white/95 text-black font-black text-xl md:text-2xl mb-2 px-3 py-1 rounded-xl shadow-md">{top3[2].score || 0} <span className="text-xs md:text-sm">pts</span></div>
            
            <div className="podium-bar w-20 md:w-24 bg-gradient-to-t from-orange-950 via-orange-700 to-orange-500 rounded-t-xl border-t-8 border-x-4 border-orange-300 flex items-start justify-center shadow-[0_0_20px_rgba(249,115,22,0.5)] relative overflow-hidden" style={{ height: podiumStep >= 1 ? '6rem' : '0' }}>
              <div className="absolute inset-0 bg-white/10 w-full h-full mix-blend-overlay"></div>
              <span className="text-5xl font-black text-orange-950 mt-4 drop-shadow-md opacity-80">3</span>
            </div>
          </div>
        )}
      </div>

      {/* RESTO DE JUGADORES */}
      <div className="w-full space-y-3 mb-8 max-w-lg z-20">
        {rest.map((p, idx) => (
          <div key={p.id} className="flex justify-between items-center p-4 bg-gray-800/90 rounded-xl border border-gray-500 shadow-lg animate-cascade backdrop-blur-md" style={{ animationDelay: `${(idx * 0.1) + 5.5}s` }}>
            <span className="text-white font-black flex items-center gap-4 text-lg">
              <span className="text-gray-400 w-6 text-right">#{idx + 4}</span>
              <span className="text-3xl">{p.avatar}</span>
              <span className="tracking-widest uppercase">{p.nickname}</span>
            </span>
            <span className="text-violet-300 font-black text-xl drop-shadow-[0_0_8px_rgba(167,139,250,0.8)]">{p.score || 0} pts</span>
          </div>
        ))}
      </div>

      {/* BOTONES DE ACCIÓN */}
      <div className={`w-full max-w-lg flex flex-col gap-4 transition-opacity duration-1000 ${podiumStep >= 3 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
           {isHost && (
               <>
                  <Button onClick={() => { updateDoc(roomRef, { status: 'closed', musicTrack: null }); onNext(); }} variant="danger" className="w-full !shadow-none !border-none !bg-red-600 hover:!bg-red-500 !text-white py-4 text-xl font-black shadow-[0_0_20px_rgba(220,38,38,0.6)]">Volver al Menú</Button>
               </>
           )}
           {!isHost && (
               <>
                  {roomStatus === 'closed' ? (
                      <Button onClick={onNext} variant="danger" className="w-full animate-fade-in !shadow-none !border-none !bg-red-600 !text-white py-4 text-xl font-black">Volver al Menú</Button>
                  ) : (
                      <div className="text-white font-black uppercase tracking-widest text-center text-sm border-2 border-white/20 bg-black/60 p-4 rounded-xl shadow-inner backdrop-blur-md">
                          <RefreshCw className="inline animate-spin mr-2"/> Esperando al anfitrión...
                      </div>
                  )}
               </>
           )}
      </div>
    </div>
  );
};

const TopPlayerList = ({ players, answers, resultsMode, correctAnswer, targetId, gameId }) => {
    return (
        <div className="flex flex-wrap justify-center gap-4 mb-6">
            {players.filter(p => !p.isSpectator).map(p => {
                const hasAnswered = !!answers?.[p.id];
                const myAns = answers?.[p.id]?.value;
                let statusClass = gameId === 'knowme' ? "bg-[#4a044e]/90 border-[#d946ef] text-white" : "bg-gray-800 border-gray-600 text-white";

                if (!resultsMode) {
                    if (hasAnswered) statusClass = gameId === 'knowme' ? "bg-blue-600 border-blue-400 text-white shadow-[0_0_10px_rgba(59,130,246,0.8)] scale-105" : "bg-yellow-600 border-yellow-400 text-white animate-pulse";
                } else {
                    if (targetId && p.id === targetId) {
                         statusClass = "bg-blue-600 border-blue-400 text-white";
                    } else if (correctAnswer !== undefined) {
                         if (myAns === correctAnswer) statusClass = "bg-green-600 border-green-400 text-white";
                         else statusClass = "bg-red-900 border-red-700 opacity-60 text-white";
                    } else {
                         statusClass = "bg-gray-700 border-gray-500 text-white";
                    }
                }

                return (
                    <div key={p.id} className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${statusClass} min-w-[80px]`}>
                        <span className="text-2xl">{p.avatar}</span>
                        <span className="text-xs font-bold truncate max-w-[100px]">{p.nickname}</span>
                        <span className="text-xs">{p.score || 0} pts</span>
                    </div>
                )
            })}
        </div>
    );
}

const AskerGame = ({ room, user, currentQ, timeLeft, submit, selected, isHost, next, roomRef }) => {
  const { playSFX } = useAudio();
  const showResults = room.roundState === 'results';
  const answersCount = Object.keys(room.answers || {}).length;
  const activePlayers = room.players.filter(p => !p.isSpectator).length;
  const lastRoundSoundPlayed = useRef(-1);

  useEffect(() => {
      if (showResults && lastRoundSoundPlayed.current !== room.currentRound) {
          lastRoundSoundPlayed.current = room.currentRound;
          const myAns = room.answers?.[user.uid]?.value;
          if (myAns !== undefined) {
              const me = room.players.find(p => p.id === user.uid);
              if (myAns === currentQ.correctIndex) {
                  playSFX('respuesta_correcta');
                  if (me && me.streak >= 1) playSFX('racha');
              } else {
                  playSFX('respuesta_incorrecta');
              }
          }
      }
  }, [showResults, room.currentRound, room.answers, user.uid, currentQ?.correctIndex, room.players, playSFX]);

  useEffect(() => {
    if (isHost && showResults && !room.scoresCalculated) {
      calculateAskerScores();
    }
  }, [showResults, isHost, room.scoresCalculated]);

  const calculateAskerScores = async () => {
    const newPlayers = room.players.map(p => {
        if (p.isSpectator) return p;
        const ans = room.answers?.[p.id];
        let addedPoints = 0;
        let newStreak = p.streak || 0;

        if (ans && ans.value === currentQ.correctIndex) {
            addedPoints = 100 + ((ans.time || 0) * 5);
            newStreak += 1;
            if (newStreak > 3) addedPoints += 50;
        } else {
            newStreak = 0;
        }

        return {
            ...p,
            score: (p.score || 0) + addedPoints,
            lastRoundPoints: addedPoints,
            streak: newStreak
        };
    });

    await updateDoc(roomRef, { players: newPlayers, scoresCalculated: true });
  };

  if (!currentQ) return <div>Preparando preguntas...</div>;

  return (
    <div className="flex flex-col gap-6">
      {/* Sistema de avatares dinámicos implementado */}
      <TopPlayerList 
        players={room.players} 
        answers={room.answers} 
        resultsMode={showResults} 
        correctAnswer={currentQ.correctIndex} 
        gameId="asker" 
      />
      
      <div className="bg-blue-900/50 p-6 rounded-2xl border border-blue-500/30 text-center shadow-[0_0_30px_rgba(30,58,138,0.5)]">
        <h2 className="text-2xl md:text-3xl font-bold text-white">{currentQ.question}</h2>
      </div>

      {!showResults ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentQ.options.map((opt, idx) => (
            <button
              key={idx}
              disabled={selected !== null}
              onClick={() => submit(idx)}
              className={`p-6 rounded-xl border-2 text-xl font-medium transition-all ${
                selected === idx 
                ? 'bg-yellow-500 border-yellow-400 text-black shadow-[0_0_15px_rgba(234,179,8,0.5)] scale-105' 
                : 'bg-[#0f0f3d]/60 border-white/10 text-white hover:border-[#6464ff] hover:shadow-[0_0_15px_#6464ff] hover:bg-[#0f0f3d]/80'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             {currentQ.options.map((opt, idx) => {
               const isCorrect = idx === currentQ.correctIndex;
               const count = Object.values(room.answers || {}).filter(a => a.value === idx).length;
               const isMyWrong = selected === idx && !isCorrect;
               
               let bgClass = 'bg-[#0f0f3d]/60 border-white/10 text-white opacity-50';
               if (isCorrect) bgClass = 'bg-green-600 border-green-400 text-white opacity-100 shadow-[0_0_15px_rgba(34,197,94,0.8)] scale-105';
               if (isMyWrong) bgClass = 'bg-red-600 border-red-400 text-white opacity-100 shadow-[0_0_15px_rgba(220,38,38,0.8)]';

               return (
                 <div key={idx} className={`p-6 rounded-xl border-2 text-xl font-medium transition-all flex justify-between items-center ${bgClass}`}>
                   <span>{opt}</span>
                   <div className="flex items-center gap-3">
                       <span className="font-bold text-base bg-black/40 px-3 py-1 rounded-full border border-white/20">{count} votos</span>
                       {isCorrect && <CheckCircle className="w-8 h-8 text-white drop-shadow-md"/>}
                       {isMyWrong && <XCircle className="w-8 h-8 text-white drop-shadow-md"/>}
                   </div>
                 </div>
               )
             })}
           </div>
           
           {isHost && <Button onClick={() => { updateDoc(roomRef, { scoresCalculated: false }); next(); }} className="w-full">Siguiente Pregunta</Button>}
           
           <GlobalRanking room={room} roomRef={roomRef} players={room.players} isFinal={false} onNext={null} gameId="asker" />
        </div>
      )}
    </div>
  );
};

const KnowMeGame = ({ room, user, currentQ, submit, selected, isHost, next, roomRef }) => {
  const { playSFX } = useAudio();
  const showResults = room.roundState === 'results';
  const activePlayers = room.players.filter(p => !p.isSpectator);
  const targetPlayer = room.players.find(p => p.id === room.knowMeTargetId) || activePlayers[room.currentRound % activePlayers.length];
  const isTarget = user.uid === targetPlayer?.id;
  const correctAnswer = targetPlayer ? room.answers?.[targetPlayer.id]?.value : undefined;
  const lastRoundSoundPlayed = useRef(-1);

  useEffect(() => {
      if (showResults && lastRoundSoundPlayed.current !== room.currentRound) {
          lastRoundSoundPlayed.current = room.currentRound;
          const myAns = room.answers?.[user.uid]?.value;
          if (myAns !== undefined && !isTarget && correctAnswer !== undefined) {
              if (myAns === correctAnswer) playSFX('respuesta_correcta');
              else playSFX('respuesta_incorrecta');
          }
      }
  }, [showResults, room.currentRound, room.answers, user.uid, isTarget, correctAnswer, playSFX]);

  useEffect(() => {
      if (isHost && room.roundState === 'active') {
          if (!targetPlayer || targetPlayer.isSpectator) {
              updateDoc(roomRef, { roundState: 'results', scoresCalculated: true });
              setTimeout(() => next(), 2000);
          }
      }
  }, [isHost, targetPlayer, room.roundState, roomRef, next]);

  useEffect(() => {
    if (isHost && showResults && !room.scoresCalculated && targetPlayer) {
        calculateScores();
    }
  }, [showResults, isHost, room.scoresCalculated, targetPlayer]);

  const calculateScores = async () => {
    const answers = room.answers || {};
    const targetAns = answers[targetPlayer.id]?.value;
    if (targetAns === undefined) {
        await updateDoc(roomRef, { scoresCalculated: true });
        return;
    }

    const correctGuessers = Object.entries(answers).filter(([uid, a]) => uid !== targetPlayer.id && a.value === targetAns).length;

    const newPlayers = room.players.map(p => {
        if (p.isSpectator) return p;
        const pAns = answers[p.id]?.value;
        let addedPoints = 0;
        let newStreak = p.streak || 0;
        
        if (p.id !== targetPlayer.id) {
            if (pAns === targetAns) {
                addedPoints = 1;
                newStreak += 1;
            } else {
                addedPoints = -1;
                newStreak = 0;
            }
        } else {
            if (correctGuessers > 0) {
                addedPoints = 1;
            } else {
                addedPoints = -1;
            }
        }
        
        return { 
            ...p, 
            score: Math.max(0, (p.score || 0) + addedPoints),
            lastRoundPoints: addedPoints,
            streak: newStreak
        };
    });
    
    await updateDoc(roomRef, { players: newPlayers, scoresCalculated: true });
  };

  const renderQuestion = () => {
      const parts = currentQ.question.split("[Nombre]");
      return (
          <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight">
              {parts.map((part, i) => (
                  <React.Fragment key={i}>
                      {part}
                      {i < parts.length - 1 && (
                          <span className="text-yellow-400 font-black px-1 uppercase neon-text">
                              {targetPlayer?.nickname || "???"}
                          </span>
                      )}
                  </React.Fragment>
              ))}
          </h3>
      );
  };

  return (
    <div className="flex flex-col gap-6">
      
      <TopPlayerList players={room.players} answers={room.answers} resultsMode={showResults} correctAnswer={correctAnswer} targetId={targetPlayer?.id} gameId="knowme" />
      <div className="bg-[#1e3a8a]/90 p-6 rounded-[2rem] border-[4px] border-[#D946EF] text-center shadow-[0_0_20px_rgba(217,70,239,0.3)]">
         {renderQuestion()}
      </div>
      {!showResults ? (
         <div className="grid grid-cols-2 gap-3">
            {currentQ.options.map((opt, idx) => (
               <button key={idx} disabled={selected !== null} onClick={() => submit(idx)}
                 className={`p-4 rounded-xl text-left border-2 transition-all ${selected === idx ? 'bg-white text-black scale-105' : 'bg-[#134e4a]/80 border-[#2dd4bf] text-[#D4FF00] hover:bg-[#134e4a]'}`}>
                 {opt}
               </button>
            ))}
            {isTarget && <div className="col-span-2 text-center text-[#D4FF00] mt-2 font-bold animate-pulse drop-shadow-md">¡TÚ ELIGES LA RESPUESTA CORRECTA!</div>}
         </div>
      ) : (
         <div className="space-y-4">
             <div className="grid grid-cols-2 gap-3">
                 {currentQ.options.map((opt, idx) => {
                    const isCorrect = idx === correctAnswer;
                    const isMyWrong = selected === idx && !isCorrect;
                    let bgClass = 'bg-[#134e4a]/50 border-transparent opacity-50 text-white';
                    if (isCorrect) bgClass = 'bg-green-600 border-green-400 opacity-100 shadow-lg text-white';
                    if (isMyWrong) bgClass = 'bg-red-600 border-red-400 opacity-100 shadow-lg text-white';
                    return (
                        <div key={idx} className={`p-4 rounded-xl border flex flex-row justify-between items-center transition-all ${bgClass}`}>
                            <span className="font-bold">{opt}</span>
                            <div className="flex items-center">
                                {isCorrect && <CheckCircle className="text-white w-6 h-6 drop-shadow-md"/>}
                                {isMyWrong && <XCircle className="text-white w-6 h-6 drop-shadow-md"/>}
                            </div>
                        </div>
                    )
                })}
             </div>
             {isHost && <Button onClick={() => { updateDoc(roomRef, { scoresCalculated: false }); next(); }} className="w-full">Siguiente Pregunta</Button>}
             <GlobalRanking room={room} roomRef={roomRef} players={room.players} isFinal={false} onNext={null} gameId="knowme" />
         </div>
      )}
    </div>
  );
};

const LikelyGame = ({ room, user, currentQ, submit, selected, isHost, next, roomRef }) => {
    const { playSFX } = useAudio();
    const showResults = room.roundState === 'results';
    useEffect(() => {
        if (isHost && showResults && !room.scoresCalculated) {
            const votes = {}; 
            const answers = room.answers || {};
            Object.values(answers).forEach(a => { votes[a.value] = (votes[a.value] || 0) + 1; });
            const newPlayers = room.players.map(p => {
                if (p.isSpectator) return p;
                const received = votes[p.id] || 0;
                return { ...p, votesReceivedTotal: (p.votesReceivedTotal || 0) + received, lastRoundPoints: 0 };
            });
            updateDoc(roomRef, { players: newPlayers, scoresCalculated: true });
        }
    }, [showResults, isHost, room.scoresCalculated, room.answers, room.players, roomRef]);
    
    let winners = [];
    if (showResults) {
        const votes = {}; 
        Object.values(room.answers || {}).forEach(a => votes[a.value] = (votes[a.value] || 0) + 1);
        let max = 0;
        Object.entries(votes).forEach(([uid, count]) => { if(count > max) max = count; });
        if(max > 0) winners = Object.keys(votes).filter(uid => votes[uid] === max);
    }
    
    return (
        <div className="flex flex-col gap-6">
            <TopPlayerList players={room.players} answers={room.answers} resultsMode={false} />
            <div className="bg-yellow-900/85 p-8 rounded-full border-4 border-yellow-500/50 text-center flex items-center justify-center min-h-[150px]">
                <h3 className="text-2xl font-black text-white leading-tight">{currentQ.text}</h3>
            </div>
            {!showResults ? (
                <div className="grid grid-cols-2 gap-4">
                    {room.players.filter(p => !p.isSpectator).map(p => (
                        <button key={p.id} disabled={selected !== null} onClick={() => { playSFX('votar'); submit(p.id); }}
                            className={`p-4 rounded-xl border font-bold transition-all text-black ${selected === p.id ? 'bg-yellow-400 border-yellow-500 scale-105 shadow-lg' : 'bg-gradient-to-b from-[#fffae6] to-[#fde6b3] border-[#e8d5a6] hover:scale-105 hover:shadow-md'}`}>
                            {p.nickname}
                        </button>
                    ))}
                </div>
            ) : (
                <div>
                     <h3 className="text-center text-2xl font-bold mb-4 text-yellow-400">Votaciones</h3>
                     <div className="flex flex-wrap gap-4 justify-center mb-6">
                        {room.players.filter(p => !p.isSpectator).map(p => {
                            const votesReceived = Object.values(room.answers || {}).filter(a => a.value === p.id).length;
                            const isWinner = winners.includes(p.id);
                            return (
                                <div key={p.id} className={`p-4 rounded text-center relative transition-all ${isWinner ? 'bg-yellow-400 scale-110 border border-yellow-500 shadow-[0_0_20px_gold] text-black' : 'bg-gradient-to-b from-[#fffae6] to-[#fde6b3] border border-[#e8d5a6] text-black opacity-60'}`}>
                                    {votesReceived > 0 && <div className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold shadow-md">{votesReceived}</div>}
                                    <div className="font-bold text-lg">{p.nickname}</div>
                                </div>
                            )
                        })}
                     </div>
                     <GlobalRanking room={room} roomRef={roomRef} players={room.players} isFinal={false} onNext={isHost ? () => { updateDoc(roomRef, { scoresCalculated: false }); next(); } : null} gameId="likely" />
                </div>
            )}
        </div>
    )
};

const RatherGame = ({ room, user, currentQ, submit, selected, isHost, next, roomRef }) => {
    const { playSFX } = useAudio();
    const showResults = room.roundState === 'results';
    const answers = Object.values(room.answers || {});
    const countA = answers.filter(a => a.value === 0).length;
    const countB = answers.filter(a => a.value === 1).length;
    const percentA = Math.round((countA / (countA + countB || 1)) * 100);
    return (
        <div className="flex flex-col h-full">
            <h2 className="text-center text-3xl font-black italic mb-6 text-orange-500 drop-shadow-md">¿QUÉ PREFIERES?</h2>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div onClick={() => { if(!showResults){ playSFX('votar'); submit(0); } }} className={`relative p-8 rounded-3xl flex flex-col items-center justify-center cursor-pointer transition-all border-4 ${selected === 0 ? 'border-white scale-105 z-10' : 'border-transparent'} bg-gradient-to-br from-orange-600 to-red-600`}>
                    <div className="text-2xl font-bold text-center text-white">{currentQ.optionA}</div>
                    {showResults && <div className="mt-4 text-5xl font-black text-white/90">{percentA}%</div>}
                </div>
                <div onClick={() => { if(!showResults){ playSFX('votar'); submit(1); } }} className={`relative p-8 rounded-3xl flex flex-col items-center justify-center cursor-pointer transition-all border-4 ${selected === 1 ? 'border-white scale-105 z-10' : 'border-transparent'} bg-gradient-to-br from-blue-600 to-cyan-600`}>
                    <div className="text-2xl font-bold text-center text-white">{currentQ.optionB}</div>
                    {showResults && <div className="mt-4 text-5xl font-black text-white/90">{100 - percentA}%</div>}
                </div>
            </div>
{showResults && (
                 <div className="mt-8 space-y-4 animate-fade-in">
                     <div className="bg-black/80 backdrop-blur-xl border-4 border-white/30 p-6 md:p-8 rounded-2xl shadow-[0_0_30px_rgba(0,0,0,0.8)]">
                        <h4 className="font-black text-xl mb-4 text-white uppercase tracking-widest text-center drop-shadow-md">Votaciones:</h4>
                        <div className="flex flex-wrap gap-3 justify-center">
                            {room.players.filter(p => !p.isSpectator).map(p => {
                                const ans = room.answers?.[p.id]?.value;
                                return ( <span key={p.id} className={`px-4 py-2 rounded-lg text-sm font-black text-white shadow-md border-2 border-white/20 uppercase tracking-wider ${ans === 0 ? 'bg-orange-600 shadow-[0_0_10px_rgba(234,88,12,0.6)]' : ans === 1 ? 'bg-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.6)]' : 'bg-gray-700'}`}>{p.nickname}</span> )
                            })}
                        </div>
                     </div>
                     {isHost && <Button onClick={next} className="w-full text-lg py-4 shadow-[0_0_15px_rgba(0,255,255,0.4)]">Siguiente Pregunta</Button>}
                 </div>
            )}
        </div>
    )
};

const ImpostorGame = ({ room, user, isHost, next, roomRef }) => {
    const { playSFX } = useAudio();
    const phase = room.phase; 
    const [inputWord, setInputWord] = useState("");

    const activePlayers = room.players.filter(p => !p.isSpectator);
    const impostorId = room.impostorId;
    const isImpostor = user.uid === impostorId;
    
    const secretWord = room.currentSecret?.word || ""; 
    const secretHint = room.currentSecret?.hint || "";
    const showHint = room.settings?.useHints;
    
    const me = room.players.find(p => p.id === user.uid);
    const isSpectator = me?.isSpectator;
    const turnOrder = room.impostorTurnOrder || []; 
    const currentTurnIdx = room.currentTurnIdx || 0;
    const currentWriterId = turnOrder[currentTurnIdx % turnOrder.length];
    const isMyTurn = user.uid === currentWriterId;
    const currentLap = Math.floor(currentTurnIdx / turnOrder.length) + 1;
    const turnsLimit = room.totalTurnsLimit || activePlayers.length * 1;

    const lastRevealPlayed = useRef(false);

    useEffect(() => {
        if (phase === 'reveal' && !lastRevealPlayed.current) {
            lastRevealPlayed.current = true;
            const myVote = room.votes?.[user.uid];
            if (myVote === impostorId) playSFX('5_impostor_detectado');
            else if (myVote !== undefined) playSFX('5_impostor_salvado');
        } else if (phase !== 'reveal') {
            lastRevealPlayed.current = false;
        }
    }, [phase, room.votes, impostorId, user.uid, playSFX]);

    const startImpostorRound = async () => {
        if (!isHost) return;
        const randomSecret = PALABRAS_IMPOSTOR[Math.floor(Math.random() * PALABRAS_IMPOSTOR.length)];
        const shuffledIds = activePlayers.map(p => p.id).sort(() => Math.random() - 0.5);
        const randomImpostorId = shuffledIds[Math.floor(Math.random() * shuffledIds.length)];

        await updateDoc(roomRef, {
            currentRound: (room.currentRound || 0) + 1,
            impostorTurnOrder: shuffledIds,
            impostorId: randomImpostorId,
            currentSecret: { word: randomSecret.word, hint: randomSecret.hint },
            currentTurnIdx: 0,
            roundState: 'active',
            phase: 'input',
            wordHistory: [],
            votes: {},
            totalTurnsLimit: activePlayers.length * 1,
            musicTrack: getRandomTrack(MUSIC_TRACKS.impostor)
        });
    };

    useEffect(() => {
        if (isHost && phase !== 'reveal') {
             const impostorPlayer = room.players.find(p => p.id === impostorId);
             if (impostorPlayer && impostorPlayer.isSpectator) {
                  startImpostorRound(); 
             } else if (phase === 'input') {
                  const writer = room.players.find(p => p.id === currentWriterId);
                  if (writer && writer.isSpectator && (currentTurnIdx < turnsLimit)) {
                       updateDoc(roomRef, { currentTurnIdx: increment(1) });
                  }
             }
        }
    }, [isHost, phase, currentWriterId, room.players, currentTurnIdx, turnsLimit, impostorId, roomRef]);

    const sendWord = async () => {
        if (!inputWord || !isMyTurn) return;
        playSFX('5_enviar_palabra_impostor');
        await updateDoc(roomRef, { wordHistory: arrayUnion({ uid: user.uid, word: inputWord, turn: currentLap -1 }), currentTurnIdx: increment(1) });
        setInputWord("");
    };

    const votePlayer = async (targetId) => {
        if (isSpectator) return;
        playSFX('votar');
        await updateDoc(roomRef, { [`votes.${user.uid}`]: targetId });
    };
    
    const votesCount = Object.keys(room.votes || {}).length;
    useEffect(() => {
        if (isHost && phase === 'voting' && votesCount >= activePlayers.length - 1) {
            updateDoc(roomRef, { phase: 'reveal', musicTrack: null });
        }
    }, [votesCount, phase, isHost, activePlayers.length, roomRef]);

    const historyWords = room.wordHistory || [];
    const isInputFinished = currentTurnIdx >= turnsLimit;

    return (
        <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 p-4 rounded-xl text-center text-white">
                {isSpectator ? (
                    <>
                        <div className="text-3xl font-black uppercase text-pink-400 tracking-widest mb-1 drop-shadow-[0_0_10px_rgba(236,72,153,0.8)]">MODO ESPECTADOR</div>
                        {isHost ? (
                            <div className="mt-4 bg-black/60 p-4 rounded-xl border border-cyan-500/50 inline-block">
                                <h3 className="text-cyan-400 text-lg uppercase mb-2 tracking-widest">PANTALLA PRINCIPAL</h3>
                                <p className="text-white font-bold">Palabra oculta para no revelar el secreto.<br/><span className="text-pink-400">¡Mirad vuestros dispositivos!</span></p>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-cyan-400 text-sm uppercase mb-2 mt-4 tracking-widest">Palabra secreta</h3>
                                <div className="text-4xl font-black text-cyan-400 tracking-widest mb-1 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">{secretWord}</div>
                            </>
                        )}
                    </>
                ) : 
                    <>
                        {isImpostor ? (
                            <>
                                <div className="text-3xl font-black text-cyan-400 tracking-widest mb-2 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">ERES EL IMPOSTOR</div>
                                {showHint && (
                                    <div className="mt-2 bg-pink-900/30 px-6 py-3 rounded-xl border border-pink-500/50 inline-block shadow-[0_0_15px_rgba(236,72,153,0.3)]">
                                        <span className="text-2xl font-black text-pink-400 uppercase tracking-widest">PISTA: {secretHint}</span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <h3 className="text-pink-400 text-sm uppercase mb-2 tracking-widest">Palabra secreta</h3>
                                <div className="text-4xl font-black text-cyan-400 tracking-widest mb-1 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">{secretWord}</div>
                            </>
                        )}
                    </>}
            </div>
            {phase === 'input' && (
                <div className="text-center">
                    <h3 className="text-white font-bold mb-4 tracking-widest uppercase">Vuelta {currentLap}</h3>
                    {!isInputFinished ? (
                        <div className="mb-6 p-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl">
                            {isMyTurn ? (
                                <div className="animate-pulse">
                                    <p className="text-cyan-400 font-bold mb-2 tracking-widest uppercase">¡ES TU TURNO!</p>
                                    <div className="flex gap-2 max-w-sm mx-auto">
                                        <Input value={inputWord} onChange={e => setInputWord(e.target.value)} placeholder="Escribe una palabra..." maxLength={20} />
                                        <Button onClick={sendWord} className="!w-auto !bg-gradient-to-r !from-pink-500 !to-orange-500 !border-none !text-white"><Send size={18}/></Button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-pink-300 tracking-widest">Turno de: <span className="text-white font-black text-lg ml-2">{room.players.find(p => p.id === currentWriterId)?.nickname}</span></p>
                            )}
                        </div>
                    ) : (
                        <div className="mb-6 text-cyan-400 font-black animate-pulse tracking-widest uppercase">¡Ronda de palabras finalizada!</div>
                    )}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                        {[0, 1, 2, 3, 4].map(turn => (
                            <div key={turn} className={`p-4 rounded-xl bg-white/5 backdrop-blur-md border ${turn === currentLap - 1 ? 'border-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.3)]' : 'border-white/10'} ${turn >= currentLap && !historyWords.some(w => w.turn === turn) ? 'hidden' : ''}`}>
                                <h4 className="text-xs font-bold text-pink-400 mb-2 uppercase tracking-widest">Vuelta {turn + 1}</h4>
                                {historyWords.filter(w => w.turn === turn).map((w, i) => { const p = room.players.find(pl => pl.id === w.uid); return ( <div key={i} className="mb-1 text-sm"><span className="font-bold text-cyan-300">{p?.nickname}:</span> <span className="text-white">{w.word}</span></div> ) })}
                            </div>
                        ))}
                    </div>
                    {isHost && isInputFinished && (
                        <div className="flex gap-2 mt-8 justify-center">
                            <Button onClick={() => updateDoc(roomRef, { totalTurnsLimit: increment(activePlayers.length) })} variant="secondary" className="!w-auto !bg-white/10 !border-white/20 text-white"><RefreshCw size={16}/> Otra Vuelta</Button>
                            <Button onClick={() => updateDoc(roomRef, { phase: 'voting' })} className="!w-auto !bg-gradient-to-r !from-pink-500 !to-orange-500 !border-none !text-white"><Vote size={16}/> Votar Ya</Button>
                        </div>
                    )}
                </div>
            )}
            {phase === 'voting' && (
                <div>
                    <div className="mb-6 grid grid-cols-3 gap-2">
                        {activePlayers.map(p => { const pWords = historyWords.filter(w => w.uid === p.id).map(w => w.word).join(", "); return ( <div key={p.id} className="bg-white/5 backdrop-blur-md border border-white/10 p-2 rounded-xl text-sm"><div className="font-black text-cyan-400">{p.nickname}</div><div className="italic text-white">{pWords}</div></div> ) })}
                    </div>
                    <h3 className="text-center text-xl font-black mb-4 text-pink-400 tracking-widest uppercase">¿Quién es el Impostor?</h3>
                    <div className="grid grid-cols-2 gap-4">
                        {activePlayers.map(p => (
                             <button key={p.id} disabled={p.isSpectator || p.id === user.uid || room.votes?.[user.uid] || isImpostor} onClick={() => votePlayer(p.id)} className={`p-4 border rounded-xl font-bold tracking-widest transition-all ${room.votes?.[user.uid] === p.id ? 'bg-cyan-600 border-cyan-400 text-white shadow-[0_0_15px_rgba(34,211,238,0.5)]' : 'bg-white/5 backdrop-blur-md border-white/10 text-white hover:bg-white/10 hover:border-pink-400'}`}>{p.nickname}</button>
                        ))}
                    </div>
                    {isImpostor && <div className="text-center mt-2 text-cyan-500 font-bold italic tracking-widest">Eres el impostor, no puedes votar.</div>}
                    <div className="text-center mt-4 text-pink-300 font-bold tracking-widest">Votos: {votesCount}/{activePlayers.length - 1}</div>
                </div>
            )}
            {phase === 'reveal' && (
                <div className="text-center">
                    <div className="text-4xl mb-4">🎭</div>
                    <h2 className="text-2xl font-black mb-2 text-white tracking-widest uppercase">El impostor era: <span className="text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">{activePlayers.find(p => p.id === impostorId)?.nickname}</span></h2>
                    <p className="text-pink-300 font-bold mb-6 tracking-widest">La palabra era: <span className="text-white">{secretWord}</span></p>
                    <div className="bg-white/5 backdrop-blur-md p-4 rounded-xl border border-cyan-500/50 mb-6"><h4 className="text-cyan-400 font-black mb-2 text-sm uppercase tracking-widest"><Search className="inline w-4 h-4 mr-1"/> Detectives que acertaron</h4><div className="flex flex-wrap gap-2 justify-center">{activePlayers.filter(p => room.votes?.[p.id] === impostorId).length > 0 ? ( activePlayers.filter(p => room.votes?.[p.id] === impostorId).map(p => ( <span key={p.id} className="bg-cyan-600 px-3 py-1 rounded-full text-xs font-bold text-white shadow-[0_0_10px_rgba(34,211,238,0.5)]">{p.nickname}</span> )) ) : ( <span className="text-pink-400 font-bold text-xs tracking-widest">Nadie lo descubrió...</span> )}</div></div>
                    {isHost && <Button onClick={startImpostorRound} className="w-full mt-8 !bg-gradient-to-r !from-pink-500 !to-orange-500 !border-none !text-white">Siguiente Ronda</Button>}
                </div>
            )}
        </div>
    );
};

const SecretGame = ({ room, user, isHost, next, roomRef }) => {
    const { playSFX } = useAudio();
    const [localSecret, setLocalSecret] = useState("");
    const activePlayers = room.players.filter(p => !p.isSpectator);
    const submittedCount = activePlayers.filter(p => room.secrets?.[p.id]).length;
    
    const me = room.players.find(p => p.id === user.uid);
    const isSpectator = me?.isSpectator;

    const lastSecretIdxPlayed = useRef(-1);

    useEffect(() => {
        if (room.phase === 'revealing' && room.revealed && lastSecretIdxPlayed.current !== room.currentSecretIdx) {
            lastSecretIdxPlayed.current = room.currentSecretIdx;
            if (room.revealSuccess) playSFX('6_secreto_revelado');
            else playSFX('6_secreto_denegado');
        } else if (!room.revealed) {
            lastSecretIdxPlayed.current = -1;
        }
    }, [room.phase, room.revealed, room.currentSecretIdx, room.revealSuccess, playSFX]);

    useEffect(() => {
        if (room.phase === 'input') setLocalSecret("");
    }, [room.phase]);

    useEffect(() => {
        if(isHost && room.phase === 'input' && submittedCount >= activePlayers.length) {
            const secretsList = Object.entries(room.secrets).map(([uid, text]) => ({ uid, text }));
            for (let i = secretsList.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [secretsList[i], secretsList[j]] = [secretsList[j], secretsList[i]];
            }
            updateDoc(roomRef, { phase: 'revealing', secretList: secretsList, currentSecretIdx: 0, revealed: false });
        }
    }, [submittedCount, isHost, room.phase, activePlayers.length, room.secrets, roomRef]);

    const submitSecret = async () => {
        if(!localSecret) return;
        playSFX('6_enviar_secreto');
        await updateDoc(roomRef, { [`secrets.${user.uid}`]: localSecret });
    };

    const currentSecretObj = room.secretList?.[room.currentSecretIdx || 0];

    const voteOwner = async (targetId) => {
        const me = room.players.find(p => p.id === user.uid);
        if (me?.isSpectator) return;
        playSFX('votar');
        await updateDoc(roomRef, { [`secretVotes.${room.currentSecretIdx}.${user.uid}`]: targetId });
    };

    const votesForCurrent = room.secretVotes?.[room.currentSecretIdx] || {};
    const votesCount = activePlayers.filter(p => votesForCurrent[p.id]).length;
    
    useEffect(() => {
        if (isHost && room.phase === 'revealing' && votesCount >= activePlayers.length && !room.revealed) {
            const correctVotes = Object.values(votesForCurrent).filter(votedId => votedId === currentSecretObj.uid).length;
            const majority = Math.floor(activePlayers.length / 2) + 1;
            const success = correctVotes >= majority;
            updateDoc(roomRef, { revealed: true, revealSuccess: success });
        }
    }, [votesCount, isHost, room.phase, room.revealed, activePlayers.length, currentSecretObj, votesForCurrent, roomRef]);

    const nextSecret = async () => {
        if (!isHost) return;
        const nextIdx = (room.currentSecretIdx || 0) + 1;
        if (nextIdx >= room.secretList.length) {
            updateDoc(roomRef, { phase: 'finished', musicTrack: null });
        } else {
            updateDoc(roomRef, { currentSecretIdx: nextIdx, revealed: false });
        }
    };

    const restartGame = async () => {
        if (!isHost) return;
        await updateDoc(roomRef, { 
            phase: 'input', 
            secrets: {}, 
            secretList: null, 
            currentSecretIdx: 0, 
            revealed: false, 
            secretVotes: {},
            musicTrack: getRandomTrack(MUSIC_TRACKS.secret) 
        });
    };

    return (
        <div>
            {(room.phase === 'input') && (
                <div className="text-center">
                    <h2 className="text-2xl font-black mb-4 text-black drop-shadow-[0_0_10px_rgba(255,255,255,1)] uppercase tracking-widest">Escribe tu oscuro secreto...</h2>
                    {user.isSpectator ? <div className="text-gray-200 font-bold">Esperando secretos...</div> : (
                        !room.secrets?.[user.uid] ? (
                            <>
                                <textarea className="w-full bg-black/60 p-4 rounded-xl text-white mb-4 h-32 border-2 border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.3)] focus:border-emerald-400 outline-none" placeholder="Yo una vez..." value={localSecret} onChange={e => setLocalSecret(e.target.value)} maxLength={300} />
                                <Button onClick={submitSecret} variant="success" className="w-full !bg-emerald-700 !text-white !border-none !shadow-[0_0_20px_rgba(16,185,129,0.8)]">CONFESAR</Button>
                            </>
                        ) : (
                            <div className="animate-pulse text-emerald-300 font-bold text-lg bg-black/50 p-4 rounded-xl border border-emerald-500/30">Esperando confesiones del resto... ({submittedCount}/{activePlayers.length})</div>
                        )
                    )}
                </div>
            )}
            {room.phase === 'revealing' && currentSecretObj && (
                <div className="flex flex-col items-center">
                    <div className="w-full bg-white text-black p-8 rounded-xl shadow-[0_0_30px_rgba(255,255,255,0.8)] rotate-1 mb-8"><p className="font-handwriting text-2xl text-center font-bold">"{currentSecretObj.text}"</p></div>
                    {!room.revealed ? (
                        <div className="bg-black/50 p-6 rounded-2xl border border-white/20 w-full backdrop-blur-md">
                            <h3 className="text-emerald-300 font-black uppercase text-sm mb-4 tracking-widest text-center">¿DE QUIÉN ES?</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full mb-6">
                                {activePlayers.map(p => ( <button key={p.id} onClick={() => voteOwner(p.id)} disabled={user.isSpectator || votesForCurrent[user.uid]} className={`p-3 font-bold rounded-xl transition-all ${votesForCurrent[user.uid] === p.id ? 'bg-emerald-500 border-emerald-400 text-white shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-105' : 'bg-gray-900 border border-gray-600 text-white hover:bg-gray-800 hover:border-emerald-500'}`}>{p.nickname}</button> ))}
                            </div>
                            <div className="text-center font-bold text-gray-300 mb-2">Votos: {votesCount}/{activePlayers.length}</div>
                        </div>
                    ) : (
                        <div className="text-center animate-bounce-in mb-6 w-full">
                            {room.revealSuccess ? ( <div className="bg-emerald-900/80 p-8 rounded-2xl border-2 border-emerald-400 shadow-[0_0_30px_rgba(16,185,129,0.6)] backdrop-blur-md"><Unlock size={64} className="mx-auto text-emerald-300 mb-4"/><h3 className="text-2xl font-black text-emerald-300 tracking-widest">¡DESCUBIERTO!</h3><p className="text-white mt-2 text-lg">El secreto pertenece a:</p><div className="text-4xl font-black text-white mt-2 uppercase drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">{room.players.find(p => p.id === currentSecretObj.uid)?.nickname}</div></div> ) : ( <div className="bg-gray-900/90 p-8 rounded-2xl border-2 border-gray-500 shadow-[0_0_30px_rgba(0,0,0,0.8)] backdrop-blur-md"><Lock size={64} className="mx-auto text-gray-400 mb-4"/><h3 className="text-2xl font-black text-gray-300 tracking-widest">SECRETO A SALVO</h3><p className="text-gray-400 mt-2 text-lg">La mayoría falló. El dueño permanece en el anonimato.</p></div> )}
                        </div>
                    )}
                    {isHost && room.revealed && ( <Button onClick={nextSecret} className="w-full mt-4 !bg-white !text-emerald-900 font-black !shadow-[0_0_20px_rgba(255,255,255,0.5)]">Siguiente Secreto</Button> )}
                </div>
            )}
            {room.phase === 'finished' && ( <div className="text-center"><h2 className="text-4xl font-black text-white mb-6 drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]">¡Secretos Revelados!</h2>{isHost && <Button onClick={restartGame} className="w-full" variant="success">Empezar Otra Partida</Button>}</div> )}
        </div>
    );
};

const DeclarationsGame = ({ room, user, isHost, roomRef }) => {
    const { playSFX } = useAudio();
    const [inputText, setInputText] = useState("");
    const [guessInput, setGuessInput] = useState("");
    const turnOrder = room.turnOrder || [];
    const currentWriterId = turnOrder[room.currentWriterIdx || 0];
    const isWriter = user.uid === currentWriterId;
    const me = room.players.find(p => p.id === user.uid);
    const isSpectator = me?.isSpectator;
    const myScore = me?.score || 0;
    const hasAttempted = room.attempts?.[user.uid];

    useEffect(() => {
        if (isHost && room.phase === 'writing') {
             const writer = room.players.find(p => p.id === currentWriterId);
             if (writer && writer.isSpectator) {
                  nextRound(); 
             }
        }
    }, [isHost, room.phase, currentWriterId, room.players, roomRef]);

    useEffect(() => {
        if (room.phase === 'writing' || room.phase === 'guessing') {
            setGuessInput("");
        }
    }, [room.phase]);

    useEffect(() => {
        if (!isHost || room.phase !== 'guessing') return;
        const activePlayersCount = room.players.filter(p => !p.isSpectator).length;
        const potentialGuessers = activePlayersCount - 1;
        const attemptsCount = Object.keys(room.attempts || {}).length;

        if (attemptsCount >= potentialGuessers) {
             updateDoc(roomRef, { roundState: 'results', phase: 'round_end', winnerId: null, timerStart: null, timerDuration: 0 });
        }
    }, [room.attempts, isHost, room.phase, room.players, roomRef]);

    const handleSendPhrase = async () => {
        if (!inputText.trim()) return;
        if (inputText.length > 50) { alert("Máximo 50 caracteres"); return; } 

        const phrase = inputText.trim();
        const length = phrase.length;
        const numToReveal = Math.floor(length * 0.50);
        const indices = new Set();
        while (indices.size < numToReveal) {
            const r = Math.floor(Math.random() * length);
            if (phrase[r] !== ' ') indices.add(r);
        }

        await updateDoc(roomRef, { 
            phase: 'guessing', 
            currentPhrase: phrase, 
            revealedIndices: Array.from(indices), 
            timerStart: serverTimestamp(),
            timerDuration: 62,
            roundState: 'active' 
        });
        setInputText("");
    };

    const buyLetter = async () => {
        if (myScore < 100) return;
        playSFX('7_comprar_letra');
        const phrase = room.currentPhrase;
        const myPrivate = room.privateRevealed?.[user.uid] || [];
        const allRevealedSet = new Set([...(room.revealedIndices || []), ...myPrivate]);
        const unrevealed = [];
        for (let i = 0; i < phrase.length; i++) { if (!allRevealedSet.has(i) && phrase[i] !== ' ') unrevealed.push(i); }
        if (unrevealed.length === 0) return;
        const randomIdx = unrevealed[Math.floor(Math.random() * unrevealed.length)];
        const newPlayers = room.players.map(p => p.id === user.uid ? { ...p, score: p.score - 100 } : p);
        await updateDoc(roomRef, { players: newPlayers, [`privateRevealed.${user.uid}`]: arrayUnion(randomIdx) });
    };

    const submitGuess = async () => {
        if (!guessInput.trim()) return;

        const normalizeStr = (str) => {
            return str.normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "") // quita tildes
                      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()¿?¡!"']/g, "") // NUEVO: quita signos de puntuación
                      .replace(/\s{2,}/g, " ") // limpia espacios extra que hayan quedado
                      .toLowerCase()
                      .trim();
        };

        const isCorrect = normalizeStr(guessInput) === normalizeStr(room.currentPhrase);
        
        if (isCorrect) {
            playSFX('7_declaracion_correcta');
            const newPlayers = room.players.map(p => {
                if (p.id === user.uid) return { ...p, score: (p.score || 0) + 1000 };
                if (p.id === currentWriterId) return { ...p, score: (p.score || 0) + 300 };
                return p;
            });
            await updateDoc(roomRef, { players: newPlayers, phase: 'round_end', winnerId: user.uid, timerStart: null, timerDuration: 0 });
        } else {
            playSFX('7_declaracion_fallida');
            await updateDoc(roomRef, { [`attempts.${user.uid}`]: true });
            setGuessInput("");
            alert("¡Incorrecto! Has gastado tu único intento.");
        }
    };

const nextRound = async () => {
        const nextIdx = (room.currentWriterIdx || 0) + 1;
        if (nextIdx >= turnOrder.length) {
            await updateDoc(roomRef, { status: 'finished', isFinalResults: false });
        } else {
             await updateDoc(roomRef, { currentWriterIdx: nextIdx, phase: 'writing', currentPhrase: "", revealedIndices: [], privateRevealed: {}, winnerId: null, attempts: {}, timerStart: null, timerDuration: 0, roundState: 'active' });
        }
    };
    
    const renderPhrase = () => {
        const phrase = room.currentPhrase || "";
        const words = phrase.split(' ');
        let globalIdx = 0;
        const myPrivate = room.privateRevealed?.[user.uid] || [];
        const visibleIndices = new Set([...(room.revealedIndices || []), ...myPrivate]);

        return (
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2"> 
                {words.map((word, wIdx) => {
                    const wordElem = ( <div key={wIdx} className="flex flex-nowrap">{word.split('').map((char) => { const currentIdx = globalIdx++; const isRevealed = visibleIndices.has(currentIdx); return ( <span key={currentIdx} className="inline-block w-6 md:w-8 text-center text-3xl font-black border-b-4 border-purple-300 mx-[2px] text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]">{isRevealed ? char : '\u00A0'}</span> ); })}</div> );
                    if (wIdx < words.length - 1) globalIdx++; 
                    return wordElem;
                })}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex justify-between items-center mb-4 bg-purple-900/50 backdrop-blur-md border border-white/20 p-3 rounded-xl"><div className="flex items-center gap-2"><span className="text-gray-300 font-bold text-xs uppercase tracking-widest">Escritor:</span><span className="font-black text-purple-300 text-lg uppercase drop-shadow-[0_0_10px_rgba(216,180,254,0.5)]">{room.players.find(p => p.id === currentWriterId)?.nickname}</span></div><div className="flex items-center gap-2 bg-black/60 px-4 py-2 rounded-full border border-yellow-500/50 shadow-inner"><Coins size={18} className="text-yellow-500"/><span className="font-black text-yellow-400">{myScore}</span></div></div>
            {room.phase === 'writing' && (
                <div className="text-center py-10">
                    {isWriter ? (
                        <div className="animate-fade-in bg-black/40 backdrop-blur-md p-8 rounded-3xl border border-purple-500/50 shadow-[0_0_30px_rgba(168,85,247,0.3)]"><h2 className="text-3xl font-black mb-4 text-purple-400 uppercase tracking-widest">¡Es tu turno de escribir!</h2><p className="text-white font-bold mb-6">Escribe una frase difícil (máx 50 caracteres).</p><input type="text" value={inputText} onChange={e => setInputText(e.target.value)} maxLength={50} className="w-full bg-purple-950/80 p-6 rounded-2xl text-center text-2xl mb-4 border-2 border-purple-400 shadow-[0_0_15px_rgba(192,132,252,0.5)] text-white focus:border-white outline-none font-bold" placeholder="..." /><div className="text-right text-xs font-bold text-purple-300 mb-6">{inputText.length}/50</div><Button onClick={handleSendPhrase} className="w-full text-lg py-4 !bg-purple-500 !text-white !border-none !shadow-[0_0_20px_rgba(168,85,247,0.8)]">Enviar Frase</Button></div>
                    ) : (
                        <div className="animate-pulse text-purple-300 flex flex-col items-center bg-black/40 backdrop-blur-md p-10 rounded-3xl border border-white/10"><Mic size={64} className="mb-6 text-purple-500 drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]"/><p className="text-xl font-bold uppercase tracking-widest text-center">Esperando a que el escritor termine su dura declaración...</p></div>
                    )}
                </div>
            )}
            {room.phase === 'guessing' && (
                <div className="flex flex-col gap-6 animate-fade-in">
                    <div className="bg-black/60 p-8 rounded-3xl border border-purple-400 shadow-[0_0_30px_rgba(192,132,252,0.4)] min-h-[150px] flex flex-col justify-center backdrop-blur-md">{renderPhrase()}</div>
                    {!isWriter && !isSpectator && ( 
                        <> 
                            {!hasAttempted ? ( 
                                <div className="flex flex-col gap-4 mb-4 bg-black/40 backdrop-blur-md p-6 rounded-2xl border border-white/10">
                                    <div className="flex gap-4 justify-center w-full">
                                        <button 
                                            onClick={buyLetter} 
                                            disabled={myScore < 100} 
                                            className={`px-6 py-4 rounded-2xl text-sm font-black uppercase tracking-widest text-black transition-all flex items-center justify-center flex-1 md:flex-none shadow-[0_0_15px_rgba(250,204,21,0.5)] ${myScore < 100 ? 'bg-gray-600 opacity-50 cursor-not-allowed' : 'bg-yellow-400 hover:bg-yellow-300 hover:scale-105'}`}
                                        >
                                            <ShoppingCart size={20} className="mr-2"/> Comprar Letra (-100)
                                        </button>
                                        <Button onClick={submitGuess} variant="primary" className="!w-auto flex-1 md:flex-none !bg-purple-500 !text-white !shadow-[0_0_15px_rgba(168,85,247,0.8)]">Resolver</Button>
                                    </div>
                                    <Input value={guessInput} onChange={e => setGuessInput(e.target.value)} placeholder="Escribe la frase exacta..." maxLength={50} className="w-full text-center text-xl font-bold py-4 bg-purple-950/50 border-purple-500 text-white" />
                                </div> 
                            ) : ( 
                                <div className="text-center text-red-100 font-black bg-red-600/80 backdrop-blur-md p-4 rounded-xl border-2 border-red-400 shadow-[0_0_20px_rgba(220,38,38,0.8)] uppercase tracking-widest text-lg">
                                    <XCircle size={24} className="inline mr-2"/> Ya has gastado tu intento
                                </div> 
                            )}
                        </> 
                    )}
                    {isWriter && ( <div className="text-center text-purple-300 font-bold italic tracking-widest text-lg mt-8 bg-black/40 p-4 rounded-xl backdrop-blur-md border border-white/10">Disfruta viendo cómo sufren...</div> )}
                </div>
            )}
            {room.phase === 'round_end' && (
                <div className="text-center py-10 animate-bounce-in bg-black/60 backdrop-blur-md p-8 rounded-3xl border border-white/20">
                    <h2 className="text-4xl font-black text-white mb-8 drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]">"{room.currentPhrase}"</h2>
                    {room.winnerId ? ( <div className="bg-green-600/80 border-2 border-green-400 p-6 rounded-2xl mb-8 shadow-[0_0_30px_rgba(34,197,94,0.6)]"><p className="text-green-100 text-sm uppercase font-black tracking-widest mb-2">¡ACERTADO POR!</p><div className="text-4xl font-black text-white uppercase drop-shadow-md">{room.players.find(p => p.id === room.winnerId)?.nickname}</div><div className="text-yellow-300 font-black mt-4 text-2xl drop-shadow-[0_0_10px_rgba(253,224,71,0.8)]">+1000 Créditos</div></div> ) : ( <div className="bg-red-900/80 border-2 border-red-500 p-6 rounded-2xl mb-8 shadow-[0_0_30px_rgba(220,38,38,0.6)]"><Clock size={48} className="mx-auto text-red-400 mb-4"/><p className="text-red-100 font-black text-2xl uppercase tracking-widest">¡NADIE ACERTÓ!</p><p className="text-lg text-white font-bold mt-2">Todos fallaron o se acabó el tiempo.</p></div> )}
                    {isHost && ( <Button onClick={nextRound} className="w-full text-xl py-4 !bg-purple-500 !text-white !shadow-[0_0_20px_rgba(168,85,247,0.8)]">Siguiente Ronda</Button> )}
                </div>
            )}
        </div>
    );
};

const RussianRouletteGame = ({ room, user, isHost, roomRef }) => {
    const { playSFX } = useAudio();
    const [spinDone, setSpinDone] = useState(false);
    const [questionInput, setQuestionInput] = useState("");
    const [answerInput, setAnswerInput] = useState("");

    const activePlayers = room.players.filter(p => !p.isSpectator && !p.hasLeft);
    const myTargetId = room.targets?.[user.uid];
    const myTarget = activePlayers.find(p => p.id === myTargetId);
    const myScore = room.players.find(p => p.id === user.uid)?.score || 0;
    
    // Manejo de expulsiones inteligente para que no se quede bloqueado
    useEffect(() => {
        if (isHost && ['write', 'answer', 'voting'].includes(room.phase)) {
            const currentActive = room.players.filter(p => !p.isSpectator && !p.hasLeft);
            const originalCount = Object.keys(room.targets || {}).length;
            const hasMismatch = currentActive.length !== originalCount || currentActive.some(p => !room.targets[p.id]);
            
            if (hasMismatch) {
                 if (currentActive.length < 2) {
                     updateDoc(roomRef, { status: 'finished', isFinalResults: false });
                     return;
                 }

                 if (room.phase === 'write') {
                     // Solo reiniciamos la ronda si alguien se va mientras escriben
                     const shuffled = [...currentActive].sort(() => Math.random() - 0.5);
                     const targets = {};
                     for(let i=0; i<shuffled.length; i++) {
                         targets[shuffled[i].id] = shuffled[(i + 1) % shuffled.length].id;
                     }
                     updateDoc(roomRef, {
                         targets: targets,
                         r_questions: [],
                         currentVoteIdx: 0,
                         roundState: 'active'
                     });
                 } else if (room.phase === 'answer' || room.phase === 'voting') {
                     // Si se expulsa en respuesta o votación, avanzamos la pregunta omitiendo al expulsado
                     const missingPlayersIds = Object.keys(room.targets).filter(tid => !currentActive.find(p => p.id === tid));
                     if (missingPlayersIds.length > 0) {
                         let updatedQs = [...(room.r_questions || [])];
                         let changed = false;
                         updatedQs = updatedQs.map(q => {
                             if ((missingPlayersIds.includes(q.targetId) || missingPlayersIds.includes(q.askerId)) && !q.skipped) {
                                 changed = true;
                                 return { ...q, skipped: true };
                             }
                             return q;
                         });
                         if (changed) {
                             updateDoc(roomRef, { r_questions: updatedQs });
                         }
                     }
                 }
            }
        }
    }, [isHost, room.players, room.phase, room.targets, roomRef, room.r_questions]);

    // Finalizar si todas las preguntas restantes fueron omitidas por expulsiones
    useEffect(() => {
        if (isHost && room.phase === 'voting') {
            const visibleQuestions = (room.r_questions || []).filter(q => !q.skipped);
            if (room.currentVoteIdx >= visibleQuestions.length && visibleQuestions.length > 0) {
                 updateDoc(roomRef, { status: 'finished', isFinalResults: false });
            }
        }
    }, [isHost, room.phase, room.currentVoteIdx, room.r_questions, roomRef]);

    useEffect(() => {
        if(room.phase === 'write') {
            setSpinDone(false);
            setQuestionInput(""); 
            const timer = setTimeout(() => setSpinDone(true), 3000);
            return () => clearTimeout(timer);
        }
    }, [room.phase]);

    const buyQuestion = async () => {
        if (questionInput.trim() !== "") {
            alert("Debes borrar el texto antes de generar una pregunta automáticamente.");
            return;
        }
        if (myScore < 100) {
            alert("No tienes suficientes créditos para comprar una pregunta.");
            return;
        }
        
        playSFX('7_comprar_letra'); 
        
        const randomQ = PREGUNTAS_RULETA[Math.floor(Math.random() * PREGUNTAS_RULETA.length)];
        setQuestionInput(randomQ);

        const newPlayers = room.players.map(p => p.id === user.uid ? { ...p, score: (p.score || 0) - 100 } : p);
        await updateDoc(roomRef, { players: newPlayers });
    };

    const submitQuestion = async () => {
        if(!questionInput.trim()) return;
        playSFX('8_enviar_pregunta');
        const qData = {
            id: generateUUID(),
            askerId: user.uid,
            targetId: myTargetId,
            text: questionInput,
            answer: null,
            skipped: false,
            votes: {}
        };
        await updateDoc(roomRef, {
            r_questions: arrayUnion(qData)
        });
    };

    useEffect(() => {
        if(isHost && room.phase === 'write') {
            const qCount = room.r_questions?.length || 0;
            if(qCount >= activePlayers.length) {
                updateDoc(roomRef, { phase: 'answer' });
            }
        }
    }, [room.r_questions, isHost, room.phase, activePlayers.length, roomRef]);

    const myQuestions = (room.r_questions || []).filter(q => q.targetId === user.uid && q.answer === null && !q.skipped);
    const currentQToAnswer = myQuestions[0]; 

    useEffect(() => {
        if (isHost && room.phase === 'answer') {
            const pendingQ = (room.r_questions || []).find(q => q.answer === null && !q.skipped);
            if (pendingQ) {
                const target = room.players.find(p => p.id === pendingQ.targetId);
                if (target && target.isSpectator) {
                    const updatedQs = room.r_questions.map(q => q.id === pendingQ.id ? { ...q, skipped: true } : q);
                    updateDoc(roomRef, { r_questions: updatedQs });
                }
            }
        }
    }, [isHost, room.phase, room.r_questions, room.players, roomRef]);

    useEffect(() => {
        if(room.phase === 'answer') {
            setAnswerInput("");
        }
    }, [room.phase, currentQToAnswer?.id]);

    const submitAnswer = async (qId, text) => {
        playSFX('8_responder');
        const updatedQs = room.r_questions.map(q => {
            if(q.id === qId) return { ...q, answer: text };
            return q;
        });
        await updateDoc(roomRef, { r_questions: updatedQs });
        setAnswerInput("");
    };

    const skipQuestion = async (qId) => {
        playSFX('8_no_responder');
        const updatedQs = room.r_questions.map(q => {
            if(q.id === qId) return { ...q, skipped: true };
            return q;
        });
        const newPlayers = room.players.map(p => p.id === user.uid ? { ...p, score: (p.score || 0) - 300 } : p);
        
        await updateDoc(roomRef, { 
            r_questions: updatedQs,
            players: newPlayers
        });
    };

    useEffect(() => {
        if(isHost && room.phase === 'answer') {
            const allDone = room.r_questions.every(q => q.answer !== null || q.skipped);
            if(allDone) {
                updateDoc(roomRef, { phase: 'voting', currentVoteIdx: 0 });
            }
        }
    }, [room.r_questions, isHost, room.phase, roomRef]);

    const visibleQuestions = (room.r_questions || []).filter(q => !q.skipped); 
    const currentVoteQ = visibleQuestions[room.currentVoteIdx];

    // Nueva función para avanzar al siguiente resultado (Sólo Anfitrión)
    const nextVotePhase = () => {
        if (!isHost) return;
        const nextIdx = (room.currentVoteIdx || 0) + 1;
        if(nextIdx >= visibleQuestions.length) {
            updateDoc(roomRef, { status: 'finished', isFinalResults: false });
        } else {
            updateDoc(roomRef, { currentVoteIdx: nextIdx });
        }
    };

    return (
        <div className="flex flex-col h-full relative">
            {room.phase === 'write' && (
                <div className="text-center mt-12">
                    {!spinDone ? (
                        <div className="flex flex-col items-center animate-pulse bg-black/60 backdrop-blur-md p-10 rounded-3xl border-2 border-white/20">
                            <RefreshCw size={80} className="text-white animate-spin mb-6 drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]" />
                            <h2 className="text-3xl font-black text-white tracking-widest uppercase">BUSCANDO OBJETIVO...</h2>
                        </div>
                    ) : (
                        <div className="animate-bounce-in bg-black/60 backdrop-blur-md p-8 rounded-3xl border-4 border-red-600 shadow-[0_0_40px_rgba(220,38,38,0.4)]">
                           <h2 className="text-xl text-white font-bold uppercase tracking-widest mb-4">Tu objetivo es</h2>
                            {/* Tamaño del nombre reducido a la mitad (text-3xl md:text-4xl) */}
                            <div className="text-3xl md:text-4xl font-black text-red-500 mb-6 uppercase drop-shadow-[0_0_20px_rgba(220,38,38,1)]">{myTarget?.nickname}</div>
                            
                            <div className="flex justify-center mb-8">
                                <div className="inline-flex items-center gap-2 bg-black/80 px-4 py-2 rounded-full border-2 border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.5)]">
                                    <Coins size={20} className="text-yellow-400"/>
                                    <span className="font-black text-white text-lg">{myScore}</span>
                                </div>
                            </div>
                            
                            {!room.r_questions?.some(q => q.askerId === user.uid) ? (
                                <>
                                    <p className="text-white text-lg mb-4 font-black uppercase tracking-widest mt-4">Hazle una pregunta anónima:</p>
                                    <textarea 
                                        className="w-full bg-black/80 border-2 border-red-500 rounded-xl p-4 text-white mb-6 h-32 text-xl font-bold shadow-inner focus:border-white outline-none"
                                        placeholder="Si no se te ocurre qué preguntar, presiona el botón rojo"
                                        value={questionInput}
                                        onChange={e => setQuestionInput(e.target.value)}
                                        maxLength={200}
                                    />
                                    <div className="flex flex-col md:flex-row gap-3">
                                        {/* Botón generar sin el icono de créditos */}
                                        <Button onClick={buyQuestion} className="w-full py-4 text-sm md:text-xl !bg-red-600 hover:!bg-red-500 !text-white !border-none !shadow-[0_0_20px_rgba(220,38,38,0.8)]" variant="danger">
                                            Random (-100 puntos)
                                        </Button>
                                        <Button onClick={submitQuestion} className="w-full py-4 text-sm md:text-xl !bg-yellow-500 hover:!bg-yellow-400 !text-black !border-none !shadow-[0_0_20px_rgba(234,179,8,0.8)]">
                                            Enviar
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-white font-black text-xl italic tracking-widest animate-pulse mt-8">Pregunta enviada. Esperando al resto...</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {room.phase === 'answer' && (
                <div className="text-center mt-8">
                    {currentQToAnswer ? (
                        <>
                            <h2 className="text-4xl font-black text-white mb-8 tracking-widest uppercase drop-shadow-[0_0_15px_rgba(255,255,255,0.8)]">DEFIÉNDETE</h2>
                            <div className="animate-fade-in bg-black/80 backdrop-blur-md border-4 border-red-600 p-8 rounded-3xl relative overflow-hidden shadow-[0_0_40px_rgba(220,38,38,0.6)]">
                                <div className="absolute top-0 left-0 w-full h-2 bg-red-500 animate-pulse shadow-[0_0_15px_rgba(239,68,68,1)]"/>
                                <p className="text-gray-300 text-sm font-bold uppercase tracking-widest mb-4">Alguien pregunta:</p>
                                <p className="text-3xl font-black text-white mb-8">"{currentQToAnswer.text}"</p>
                                
                                <textarea 
                                    className="w-full bg-black border-2 border-gray-500 rounded-xl p-4 text-white mb-6 h-32 text-xl font-bold shadow-inner focus:border-white outline-none"
                                    placeholder="Tu respuesta..."
                                    value={answerInput}
                                    onChange={e => setAnswerInput(e.target.value)}
                                    maxLength={250}
                                />
                                <div className="flex flex-col md:flex-row gap-3 mt-4">
                                    <Button onClick={() => skipQuestion(currentQToAnswer.id)} variant="outline" className="flex-1 border-2 border-red-600 text-red-500 hover:bg-red-600 hover:text-white font-bold py-3 text-sm md:text-base">
                                        <ShieldAlert size={18} className="mr-1 md:mr-2"/> No Contestar (-300)
                                    </Button>
                                    <Button onClick={() => submitAnswer(currentQToAnswer.id, answerInput)} variant="primary" className="flex-1 !bg-white !text-black py-3 text-base md:text-xl !shadow-[0_0_15px_rgba(255,255,255,0.8)] font-black">
                                        Responder
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-white font-black italic flex flex-col items-center bg-black/60 backdrop-blur-md p-10 rounded-3xl border-2 border-white/20">
                            <Clock size={80} className="mb-6 text-yellow-500 animate-pulse drop-shadow-[0_0_15px_rgba(234,179,8,0.8)]"/>
                            <p className="text-2xl uppercase tracking-widest">Esperando las respuestas de los demás...</p>
                        </div>
                    )}
                </div>
            )}

            {room.phase === 'voting' && currentVoteQ && (
                <div className="flex flex-col items-center mt-4 animate-fade-in w-full">
                    <div className="text-center mb-8">
                        <span className="text-sm text-gray-300 font-bold uppercase tracking-widest">PREGUNTA PARA</span>
                        <div className="text-5xl font-black text-white uppercase drop-shadow-[0_0_20px_rgba(255,255,255,0.8)] mt-2">
                            {room.players.find(p => p.id === currentVoteQ.targetId)?.nickname}
                        </div>
                    </div>
                    
                    <div className="w-full bg-black/80 backdrop-blur-md p-8 rounded-3xl border-4 border-gray-600 mb-8 relative shadow-[0_0_30px_rgba(0,0,0,0.8)]">
                        <div className="absolute -top-4 left-8 bg-red-600 text-white text-sm px-4 py-1 rounded-full font-black tracking-widest border-2 border-white shadow-lg">PREGUNTA</div>
                        <p className="text-2xl font-black text-white mb-8 mt-4">"{currentVoteQ.text}"</p>
                        
                        <div className="border-t-4 border-gray-700 pt-8 relative">
                            <div className="absolute -top-4 left-8 bg-blue-600 text-white text-sm px-4 py-1 rounded-full font-black tracking-widest border-2 border-white shadow-lg">RESPUESTA</div>
                            <p className="text-2xl text-gray-200 font-bold italic">"{currentVoteQ.answer}"</p>
                        </div>
                    </div>

                    {isHost ? (
                        <Button 
                            onClick={nextVotePhase} 
                            className="w-full mt-2 py-4 text-xl !bg-blue-600 hover:!bg-blue-500 !text-white !border-none shadow-[0_0_20px_rgba(37,99,235,0.8)]"
                        >
                            Siguiente Pregunta
                        </Button>
                    ) : (
                        <div className="text-white font-black text-2xl uppercase tracking-widest italic animate-pulse bg-black/60 backdrop-blur-md p-6 rounded-2xl border-2 border-white/20 w-full text-center">
                            Esperando al anfitrión...
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const JudgmentGame = ({ room, user, isHost, roomRef }) => {
    const { playSFX } = useAudio();
    const activePlayers = room.players.filter(p => !p.isSpectator);
    const roundData = room.judgmentRounds?.[room.currentJudgmentIdx || 0];
    const targetPlayer = room.players.find(p => p.id === roundData?.targetId);
    const quality = roundData?.quality;
    const isTarget = user.uid === targetPlayer?.id;
    const me = room.players.find(p => p.id === user.uid);

    const votes = room.j_votes || {};
    const votesCount = Object.keys(votes).length;
    const requiredVotes = activePlayers.length > 1 ? activePlayers.length - 1 : 1;

    const lastJudgmentIdx = useRef(-1);

    useEffect(() => {
        if (room.phase === 'results' && lastJudgmentIdx.current !== room.currentJudgmentIdx) {
            lastJudgmentIdx.current = room.currentJudgmentIdx;
            playSFX('9_resultado');
        }
    }, [room.phase, room.currentJudgmentIdx, playSFX]);

    useEffect(() => {
        if (isHost && room.phase === 'voting') {
            if (targetPlayer && targetPlayer.isSpectator) {
                 nextQuestion(); 
            }
        }
    }, [isHost, room.phase, room.players, targetPlayer, roomRef]);

    const submitVote = async (val) => {
        if (isTarget || me?.isSpectator) return;
        if (val === -1) playSFX('9_nose');
        else playSFX('votar');
        
        await updateDoc(roomRef, { [`j_votes.${user.uid}`]: val });
    };

    useEffect(() => {
        if (isHost && room.phase === 'voting' && votesCount >= requiredVotes) {
            updateDoc(roomRef, { phase: 'results' });
        }
    }, [votesCount, isHost, room.phase, requiredVotes, roomRef]);

    const nextQuestion = async () => {
        const nextIdx = (room.currentJudgmentIdx || 0) + 1;
        if (nextIdx >= room.judgmentRounds.length) {
            updateDoc(roomRef, { phase: 'finished_judgment' });
        } else {
            updateDoc(roomRef, { currentJudgmentIdx: nextIdx, phase: 'voting', j_votes: {} });
        }
    };

    const startJudgmentRound = async () => {
        const shuffledTargets = [...activePlayers].sort(() => Math.random() - 0.5);
        const rounds = shuffledTargets.map(target => ({
            targetId: target.id, 
            quality: CUALIDADES_JUICIO[Math.floor(Math.random() * CUALIDADES_JUICIO.length)]
        }));
        await updateDoc(roomRef, {
            judgmentRounds: rounds,
            currentJudgmentIdx: 0,
            phase: 'voting',
            j_votes: {}
        });
    };

    if (room.phase === 'voting') {
        if (me?.isSpectator) {
            return (
                <div className="text-center animate-fade-in py-12 bg-black/60 backdrop-blur-md rounded-3xl border-2 border-amber-900/50 shadow-[0_0_30px_rgba(0,0,0,0.8)]">
                    <Gavel size={80} className="mx-auto text-amber-600 mb-8 animate-pulse drop-shadow-[0_0_15px_rgba(217,119,6,0.8)]" />
                    <h2 className="text-3xl font-black mb-6 text-gray-300 uppercase tracking-widest">Modo Espectador</h2>
                    <p className="text-2xl text-white font-bold">Esperando que el tribunal decida la nota de <span className="text-amber-400 font-black uppercase drop-shadow-md">{targetPlayer?.nickname}</span>...</p>
                </div>
            )
        }

        if (isTarget) {
            return (
                <div className="text-center animate-fade-in py-12 bg-black/60 backdrop-blur-md rounded-3xl border-4 border-amber-600 shadow-[0_0_40px_rgba(217,119,6,0.5)]">
                    <Gavel size={80} className="mx-auto text-amber-500 mb-8 animate-bounce drop-shadow-[0_0_20px_rgba(245,158,11,0.8)]" />
                    <h2 className="text-3xl md:text-5xl font-black mb-10 text-white drop-shadow-md">Están evaluando tu nota en <br/><span className="font-black text-amber-400 uppercase underline decoration-amber-600 mt-4 inline-block drop-shadow-[0_0_15px_rgba(245,158,11,0.8)]">{quality}</span></h2>
                    <div className="animate-pulse text-amber-200 font-bold mt-10 text-2xl tracking-widest uppercase bg-black/40 inline-block px-8 py-4 rounded-full border border-amber-500/30">Esperando el veredicto... ({votesCount}/{requiredVotes})</div>
                </div>
            )
        }

        return (
            <div className="text-center animate-fade-in py-8 bg-black/60 backdrop-blur-md rounded-3xl border-2 border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.8)] px-4">
                <Gavel size={64} className="mx-auto text-amber-500 mb-6 drop-shadow-[0_0_15px_rgba(245,158,11,0.8)]" />
                <h2 className="text-2xl md:text-4xl font-black mb-10 text-white leading-tight">¿Qué nota le das a <span className="font-black text-amber-400 uppercase drop-shadow-[0_0_15px_rgba(245,158,11,0.8)]">{targetPlayer?.nickname}</span> en <br/><span className="font-black text-white uppercase underline decoration-amber-500 mt-2 inline-block">{quality}</span>?</h2>
                {!votes[user.uid] ? (
                    <div className="grid grid-cols-5 gap-3 max-w-lg mx-auto">
                        {[1,2,3,4,5,6,7,8,9,10].map(n => (
                            <button key={n} onClick={() => submitVote(n)} className="bg-amber-900/80 border-4 border-amber-600 text-white p-4 md:p-6 rounded-2xl font-black text-2xl hover:bg-amber-500 hover:scale-110 transition-all shadow-[0_0_15px_rgba(217,119,6,0.5)]">{n}</button>
                        ))}
                        <button onClick={() => submitVote(-1)} className="col-span-5 bg-gray-900 border-4 border-gray-600 text-white p-6 rounded-2xl font-black text-2xl mt-4 hover:bg-gray-800 transition-all uppercase tracking-widest shadow-lg hover:scale-105">No sé</button>
                    </div>
                ) : (
                    <div className="animate-pulse text-amber-200 font-bold mt-10 text-2xl tracking-widest uppercase bg-black/40 inline-block px-8 py-4 rounded-full border border-amber-500/30">Voto emitido. Esperando al tribunal... ({votesCount}/{requiredVotes})</div>
                )}
            </div>
        )
    }

    if (room.phase === 'results') {
        const validVotes = Object.values(votes).filter(v => v !== -1);
        const average = validVotes.length > 0 ? (validVotes.reduce((a,b) => a+b, 0) / validVotes.length).toFixed(1) : '-';

        return (
            <div className="text-center animate-fade-in py-10 bg-black/60 backdrop-blur-md rounded-3xl border-2 border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.8)] px-4">
                <h2 className="text-xl md:text-2xl text-gray-300 font-bold mb-4 uppercase tracking-widest">Nota media de <span className="text-white font-black">{targetPlayer?.nickname}</span> en <span className="text-amber-400">{quality}</span></h2>
                <div className="text-8xl md:text-9xl font-black text-amber-400 mb-12 drop-shadow-[0_0_30px_rgba(251,191,36,0.8)] bg-black/80 py-8 rounded-[3rem] border-4 border-amber-600 inline-block px-16 shadow-inner">{average}</div>

                {!room.settings?.anonymousVotes && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12 text-lg max-w-3xl mx-auto">
                        {activePlayers.map(p => {
                            if (p.id === targetPlayer.id) return null; 
                            const v = votes[p.id];
                            return (
                                <div key={p.id} className="bg-gray-900/80 p-4 rounded-xl flex justify-between items-center border-2 border-gray-600 shadow-md">
                                    <span className="truncate mr-2 font-bold text-white uppercase tracking-wider">{p.nickname}</span>
                                    <span className="font-black text-amber-400 text-2xl drop-shadow-[0_0_5px_rgba(251,191,36,0.8)]">{v === -1 ? 'N/S' : v}</span>
                                </div>
                            )
                        })}
                    </div>
                )}

                {isHost && <Button onClick={nextQuestion} className="w-full max-w-md mx-auto text-2xl py-4 !bg-amber-500 !text-black !border-none shadow-[0_0_25px_rgba(245,158,11,0.6)]">Siguiente Pregunta</Button>}
            </div>
        )
    }

    if (room.phase === 'finished_judgment') {
         return (
             <div className="text-center animate-fade-in py-12 bg-black/60 backdrop-blur-md rounded-3xl border-2 border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.8)] px-4">
                <Gavel size={80} className="mx-auto text-amber-500 mb-8 drop-shadow-[0_0_15px_rgba(245,158,11,0.8)]" />
                <h2 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase tracking-widest drop-shadow-md">¡Juicio Finalizado!</h2>
                <p className="text-xl text-amber-200 font-bold mb-10 italic">Esperamos que no haya resentimientos.</p>
                {isHost ? (
                    <div className="flex flex-col gap-4 max-w-md mx-auto">
                        <Button onClick={startJudgmentRound} className="w-full py-4 text-xl !bg-green-500 !text-black !border-none shadow-[0_0_25px_rgba(34,197,94,0.6)]">Hacer otra ronda</Button>
                        <Button onClick={() => updateDoc(roomRef, { status: 'closed' })} variant="danger" className="w-full py-4 text-xl !bg-red-600 !text-white !shadow-none !border-none">Finalizar Partida</Button>
                    </div>
                ) : (
                    <div className="text-white font-black text-xl tracking-widest uppercase animate-pulse mt-8 bg-black/40 inline-block px-8 py-4 rounded-full border border-white/20">Esperando al anfitrión...</div>
                )}
             </div>
         )
    }

    return null;
};

// --- 5. MOTOR DE JUEGO (GameEngine) ---
const GameEngine = ({ room, user, roomRef, gameId, onShowInfo, onOpenKick, onLeaveRoom }) => {
  const { playSFX } = useAudio();
  const isHost = room.hostId === user.uid;
  const me = room.players.find(p => p.id === user.uid);
  const currentQ = room.content?.[room.currentRound];
  const [timeLeft, setTimeLeft] = useState(20);
  const [selectedAnswer, setSelectedAnswer] = useState(null);

  const gameConfig = GAMES.find(g => g.id === gameId);
  const gongPlayedForRound = useRef(-1);

  useEffect(() => {
    setSelectedAnswer(null);
  }, [room.currentRound]);

  useEffect(() => {
     if (room.roundState === 'active' && room.answers?.[user.uid] && selectedAnswer === null) {
         setSelectedAnswer(room.answers[user.uid].value);
     }
  }, [room.answers, user.uid, room.roundState, selectedAnswer]);

  useEffect(() => {
    if (room.roundState !== 'active') return;
    if (room.timerStart && room.timerDuration > 0) {
const calcTime = () => {
          const startMillis = room.timerStart?.toMillis ? room.timerStart.toMillis() : Date.now();
          const elapsed = (Date.now() - startMillis) / 1000;
          const seconds = Math.ceil(room.timerDuration - elapsed);
          return seconds > 0 ? seconds : 0;
      };
      
      setTimeLeft(calcTime()); 

      const interval = setInterval(() => {
        const seconds = calcTime();
        setTimeLeft(seconds);

        if (isHost && seconds <= 0) {
          handleTimeUp();
          clearInterval(interval);
        }
      }, 500);

      // ¡Asegúrate de no haber borrado esta línea de abajo ni las llaves!
      return () => clearInterval(interval);
    } else {
        setTimeLeft(99); 
    }
  }, [room.roundState, room.timerStart, room.timerDuration, isHost, gameId, roomRef, room.currentRound, playSFX]);

  const handleTimeUp = async () => {
    if (!isHost) return;
    if (gameId === 'declarations') {
         await updateDoc(roomRef, { 
             roundState: 'results',
             phase: 'round_end',
             winnerId: null 
         });
    } else {
        await updateDoc(roomRef, { roundState: 'results' });
    }
  };

  const nextRound = async () => {
    if (!isHost) return;
    
    if (gameId === 'russian') {
        const activePlayers = room.players.filter(p => !p.isSpectator);
        const shuffled = [...activePlayers].sort(() => Math.random() - 0.5);
        const targets = {};
        for(let i=0; i<shuffled.length; i++) {
            const asker = shuffled[i];
            const target = shuffled[(i + 1) % shuffled.length];
            targets[asker.id] = target.id;
        }
        
        await updateDoc(roomRef, {
            targets: targets,
            phase: 'write',
            r_questions: [],
            currentVoteIdx: 0,
            roundState: 'active'
        });
        return;
    }

    const nextR = room.currentRound + 1;
    if (nextR >= room.content.length && gameId !== 'declarations' && gameId !== 'russian') {
      await updateDoc(roomRef, { status: 'finished', musicTrack: null });
    } else {
      const duration = (gameConfig?.timer || 20);
      
      let updateData = { 
        currentRound: nextR, 
        roundState: 'active', 
        timerStart: duration > 0 ? serverTimestamp() : null,
        timerDuration: duration > 0 ? duration + 2 : 0,
        answers: {}, 
        votes: {}, 
        scoresCalculated: false,
        phase: 'input', 
        impostorTurn: 0,
        wordHistory: [],
        currentSecretIdx: 0,
        secretList: null,
        revealed: false,
        secrets: {} 
      };

      if (gameId === 'knowme') {
          const activePlayers = room.players.filter(p => !p.isSpectator);
          updateData.knowMeTargetId = activePlayers[nextR % activePlayers.length]?.id;
      }

      await updateDoc(roomRef, updateData);
    }
  };

  const submitAnswer = async (val) => {
    if (me?.isSpectator) return;
    if (selectedAnswer !== null || room.roundState !== 'active') return;
    
    setSelectedAnswer(val);
    
    const answerData = { value: val, time: timeLeft };
    await updateDoc(roomRef, {
      [`answers.${user.uid}`]: answerData
    });
  };

  useEffect(() => {
      if(isHost && room.roundState === 'active') {
          const activePlayers = room.players.filter(p => !p.isSpectator);
          const answersCount = Object.keys(room.answers || {}).length;
          if(['asker', 'knowme', 'likely', 'rather'].includes(gameId)) {
              if(answersCount >= activePlayers.length) {
                  updateDoc(roomRef, { roundState: 'results' });
              }
          }
      }
  }, [room.answers, isHost, room.roundState, gameId, room.players, roomRef]);

  const renderGame = () => {
    if (gameId === 'asker') return <AskerGame room={room} user={user} currentQ={currentQ} timeLeft={timeLeft} submit={submitAnswer} selected={selectedAnswer} isHost={isHost} next={nextRound} roomRef={roomRef}/>;
    if (gameId === 'knowme') return <KnowMeGame room={room} user={user} currentQ={currentQ} timeLeft={timeLeft} submit={submitAnswer} selected={selectedAnswer} isHost={isHost} next={nextRound} roomRef={roomRef}/>;
    if (gameId === 'likely') return <LikelyGame room={room} user={user} currentQ={currentQ} timeLeft={timeLeft} submit={submitAnswer} selected={selectedAnswer} isHost={isHost} next={nextRound} roomRef={roomRef}/>;
    if (gameId === 'rather') return <RatherGame room={room} user={user} currentQ={currentQ} timeLeft={timeLeft} submit={submitAnswer} selected={selectedAnswer} isHost={isHost} next={nextRound} roomRef={roomRef}/>;
    if (gameId === 'impostor') return <ImpostorGame room={room} user={user} isHost={isHost} next={nextRound} roomRef={roomRef}/>;
    if (gameId === 'secret') return <SecretGame room={room} user={user} isHost={isHost} next={nextRound} roomRef={roomRef}/>;
    if (gameId === 'declarations') return <DeclarationsGame room={room} user={user} isHost={isHost} roomRef={roomRef} />;
    if (gameId === 'russian') return <RussianRouletteGame room={room} user={user} isHost={isHost} roomRef={roomRef} />;
    if (gameId === 'judgment') return <JudgmentGame room={room} user={user} isHost={isHost} roomRef={roomRef} />;
    return <div>Cargando juego...</div>;
  };

  const hideHeaderInfo = ['impostor', 'secret', 'declarations', 'russian', 'judgment'].includes(gameId);
  
  const showTimer = gameConfig?.timer > 0 && !(
      (gameId === 'declarations' && room.phase === 'writing') ||
      (gameId === 'russian' && room.phase === 'write')
  );

  let headerClasses = "flex flex-wrap md:flex-nowrap justify-between items-center mb-8 p-3 md:p-4 backdrop-blur-md gap-y-3 relative ";
  let titleClasses = "text-base md:text-2xl font-black uppercase tracking-tight truncate max-w-[120px] sm:max-w-[200px] md:max-w-none ";
  
  if (gameId === 'asker') {
      headerClasses += "bg-black/40 rounded-xl border border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.5)] ";
      titleClasses += "text-white ";
  } else if (gameId === 'knowme') {
      headerClasses += "bg-[#1f2937]/90 rounded-2xl shadow-2xl ";
      titleClasses += "text-[#D4FF00] ";
  } else if (gameId === 'likely') {
      headerClasses += "bg-gradient-to-b from-[#fffae6] to-[#fde6b3] border-4 border-yellow-400 rounded-xl ";
      titleClasses += "text-gray-900 ";
  } else if (gameId === 'declarations') {
      headerClasses += "bg-black/40 rounded-2xl border border-white/10 ";
      titleClasses += "text-white ";
  } else {
      headerClasses += "bg-black/40 rounded-xl border border-white/10 text-white ";
      if (gameId === 'impostor') {
          titleClasses += "text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] ";
      } else {
          titleClasses += "text-white ";
      }
  }

  return (
    <div className="w-full max-w-4xl mx-auto mt-4 p-4 relative">
      
      {['asker', 'knowme', 'likely', 'rather'].includes(gameId) && (
        <div className="text-center mb-4 animate-fade-in">
            <span className="text-[10px] text-white uppercase tracking-widest block">Tema Escogido</span>
            <span className="text-2xl md:text-3xl font-black text-white uppercase text-shadow-lg neon-text-subtle drop-shadow-md">
                {room.settings?.topic || 'General'}
            </span>
        </div>
      )}

      {/* HEADER DE JUEGO ADAPTADO */}
      <div className={headerClasses}>
<div className="flex flex-col items-start gap-1 flex-1 min-w-[30%]">
           <div className="flex items-center gap-1 md:gap-2">
               <span className={titleClasses}>{gameConfig?.title}</span>
               <button onClick={onShowInfo} className="text-white/50 hover:text-white transition-colors shrink-0">
                   <Info size={16} className="md:w-5 md:h-5" />
               </button>
           </div>
           
           {!hideHeaderInfo && (
               <div className="bg-black/50 border border-white/20 px-3 py-1 rounded-full flex items-center gap-2 shadow-inner mt-1">
                <span className="text-[10px] font-black text-white uppercase tracking-widest opacity-80">Ronda</span>
                <span className="text-xs font-black text-white leading-none">{room.currentRound + 1} / {room.content?.length || '?'}</span>
               </div>
           )}
        </div>
        
        {showTimer && (
            <div className={`order-first md:order-none w-full md:w-auto flex justify-center mb-2 md:mb-0 relative px-4 py-2 md:px-6 md:py-2 rounded-xl border-2 md:border-4 shadow-[0_0_15px_rgba(0,0,0,0.5)] bg-black mx-auto ${timeLeft < 5 ? 'border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.8)]' : 'border-white/20'}`}>
            <span className={`text-4xl md:text-6xl font-mono font-black ${timeLeft < 5 ? 'text-red-500 animate-ping' : 'text-white'}`}>
                {room.roundState === 'active' ? timeLeft : '--'}
            </span>
            </div>
        )}

        <div className="flex items-center justify-end gap-2 flex-1 min-w-[40%] z-10">
            {isHost ? (
              <>
                  <Button onClick={onOpenKick} variant="danger" className="text-[10px] md:text-xs px-2 py-1 md:px-3 md:py-1 h-8 md:h-10 whitespace-nowrap !bg-red-600 !text-white !shadow-none !border-none !w-auto"><UserMinus size={14} className="md:mr-1"/><span className="hidden md:inline">Expulsar</span></Button>
                  <Button onClick={() => updateDoc(roomRef, { status: 'closed', musicTrack: null })} variant="danger" className="text-[10px] md:text-xs px-2 py-1 md:px-3 md:py-1 h-8 md:h-10 whitespace-nowrap shadow-none !bg-red-600 !text-white !border-none !w-auto"><X size={14} className="md:mr-1"/><span className="hidden md:inline">Cerrar Sala</span></Button>
              </>
            ) : (
              <Button onClick={onLeaveRoom} variant="danger" className="text-[10px] md:text-xs px-2 py-1 md:px-3 md:py-1 h-8 md:h-10 whitespace-nowrap shadow-none !bg-red-600 !text-white !border-none !w-auto"><LogOut size={14} className="md:mr-1"/><span className="hidden md:inline">Abandonar</span></Button>
            )}
        </div>
      </div>
      
      <div className={room.roundState === 'waiting' ? 'opacity-50 pointer-events-none filter blur-[1px] transition-all duration-500' : 'opacity-100 transition-all duration-500'}>
         {me?.isSpectator && !['impostor', 'judgment'].includes(gameId) && (
             <div className="bg-red-900/80 border border-red-500 text-white font-black text-center py-2 px-4 rounded-xl mb-6 uppercase tracking-widest animate-pulse shadow-[0_0_15px_rgba(220,38,38,0.5)]">
                 Modo Espectador
             </div>
         )}
         {renderGame()}
      </div>

    </div>
  );
};

// --- 6. APP MAIN COMPONENT ---

export default function App() {
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [view, setView] = useState("hub");
  const [selectedGame, setSelectedGame] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nickname, setNickname] = useState("");
  const [inputPin, setInputPin] = useState("");
  
  const [hostPlays, setHostPlays] = useState(() => {
      if (typeof window !== 'undefined') {
          const saved = localStorage.getItem('asker_host_plays');
          return saved !== null ? saved === 'true' : true;
      }
      return true;
  });

  const [settings, setSettings] = useState({});

  // Modales
  const [kickModalOpen, setKickModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showBugReport, setShowBugReport] = useState(false);
  const [showGameInfo, setShowGameInfo] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [managingSub, setManagingSub] = useState(false); 

  // Audio System
  const [isMuted, setIsMuted] = useState(false);
  const [isMusicMuted, setIsMusicMuted] = useState(false);
  const musicAudioRef = useRef(null);

  const playSFX = (soundName) => {
      if (isMuted) return;
      const audio = new Audio(`/sounds/${soundName}.mp3`);
      audio.play().catch(e => console.warn("SFX error:", e));
  };

  const roomsRef = (id) => doc(db, 'artifacts', appId, 'public', 'data', 'rooms', id);
  const roomDataRef = useRef(roomData);

  useEffect(() => {
      roomDataRef.current = roomData;
  }, [roomData]);

// Gestor de Música
  useEffect(() => {
      let currentAudio = musicAudioRef.current;
      const isHost = roomData?.hostId === user?.uid;
      const currentMusicMode = roomData?.settings?.musicMode || 'host';
      const shouldPlayMusic = currentMusicMode === 'all' || (currentMusicMode === 'host' && isHost);

      if (!roomData?.musicTrack || !shouldPlayMusic || isMusicMuted) {
          if (currentAudio) {
              currentAudio.pause();
              currentAudio.currentTime = 0;
          }
          return;
      }

      if (!currentAudio || currentAudio.src.indexOf(roomData.musicTrack) === -1) {
          if (currentAudio) currentAudio.pause();
          const newAudio = new Audio(`/sounds/${roomData.musicTrack}.mp3`);
          newAudio.loop = true;
          newAudio.play().catch(e => console.warn("Music error:", e));
          musicAudioRef.current = newAudio;
      } else if (currentAudio.paused) {
          currentAudio.play().catch(e => console.warn("Music play error:", e));
      }

  }, [roomData?.musicTrack, roomData?.settings?.musicMode, roomData?.hostId, user?.uid, isMusicMuted]);

  useEffect(() => {
      const handleBeforeUnload = (e) => {
          const currentRoom = roomDataRef.current;
          if (roomId && user && currentRoom) {
              const me = currentRoom.players?.find(p => p.id === user.uid);
              if (me && !me.isHost && !me.isSpectator && !me.hasLeft) {
                  const newPlayers = currentRoom.players.map(p => 
                      p.id === user.uid ? { ...p, isSpectator: true, hasLeft: true } : p
                  );
                  updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rooms', roomId), {
                      players: newPlayers,
                      lastNotification: { id: generateUUID(), message: `${me.nickname} ha abandonado la sala.`, timestamp: Date.now() }
                  }).catch(() => {});
              }
          }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [roomId, user, db, appId]);

 const localSessionId = useRef(null);
  if (!localSessionId.current) {
      if (typeof window !== 'undefined') {
          localSessionId.current = sessionStorage.getItem('asker_session_id') || generateUUID();
          sessionStorage.setItem('asker_session_id', localSessionId.current);
      } else {
          localSessionId.current = generateUUID();
      }
  }

  useEffect(() => {
    let userUnsubscribe = null;
    let heartbeatInterval = null;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
          
          if (currentUser.isAnonymous) {
             setUser(currentUser);
             setIsPremium(false);
          } else {
             const userRef = doc(db, 'artifacts', appId, 'users', currentUser.uid);
             const snap = await getDoc(userRef);
             let data = snap.exists() ? snap.data() : null;
             
             // NUEVA LÓGICA: Control de 1 solo dispositivo a la vez (Bloqueo de nuevo login)
             if (data && data.currentSessionId && data.currentSessionId !== localSessionId.current) {
                 // Comprobar si la otra sesión está activa (latido en los últimos 20 seg)
                 if (data.lastHeartbeat && (Date.now() - data.lastHeartbeat < 20000)) {
                     alert("Esta cuenta esta siendo usada en otro dispositivo ahora mismo");
                     signOut(auth);
                     resetApp();
                     return; // Bloqueamos la entrada
                 }
             }

             setUser(currentUser);

             if (data) {
                 setIsPremium(!!data.isPremium);
             } else {
                 await setDoc(userRef, {
                     email: currentUser.email,
                     isPremium: false,
                     subscriptionType: null,
                     createdAt: Date.now(),
                     lastLoginAt: Date.now()
                 }, { merge: true });
                 setIsPremium(false);
             }
             
             // Registramos nuestra sesión como la activa
             await setDoc(userRef, { 
                 currentSessionId: localSessionId.current,
                 lastHeartbeat: Date.now()
             }, { merge: true });

             // Latido para indicar que seguimos conectados (cada 10 seg)
             heartbeatInterval = setInterval(() => {
                 updateDoc(userRef, { lastHeartbeat: Date.now() }).catch(() => {});
             }, 10000);

             userUnsubscribe = onSnapshot(userRef, (docSnap) => {
                 if (docSnap.exists()) {
                     const docData = docSnap.data();
                     if (docData.currentSessionId && docData.currentSessionId !== localSessionId.current) {
                         signOut(auth);
                         resetApp();
                     }
                 }
             });
          }

      } else {
        if (userUnsubscribe) userUnsubscribe();
        if (heartbeatInterval) clearInterval(heartbeatInterval);
        signInAnonymously(auth);
      }
    });

    return () => {
        unsubscribe();
        if (userUnsubscribe) userUnsubscribe();
        if (heartbeatInterval) clearInterval(heartbeatInterval);
    };
  }, []);

  useEffect(() => {
    if (!roomId || !user) return;
    const unsub = onSnapshot(roomsRef(roomId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        setRoomData(data);
        
        if (data.status === 'closed') {
            if (view === 'results' && data.isFinalResults) {
                return;
            } else {
                resetApp();
                return;
            }
        }

        if (data.status === 'playing' && view !== 'game') setView('game');
        if (data.status === 'finished' && view !== 'results') setView('results');

        if (data.gameId && !selectedGame) {
            setSelectedGame(GAMES.find(g => g.id === data.gameId));
        }

        if (view === 'lobby' && data.hostId === user.uid) {
            const playerCount = data.players.length;
            if (playerCount > 2 && !isPremium && !showSubscription) {
                setShowSubscription(true);
            }
        }

      } else {
        alert("La sala ha sido cerrada.");
        resetApp();
      }
    }, (error) => console.error("Error sala:", error));
    return () => unsub();
  }, [roomId, user, view, isPremium, roomData?.isFinalResults]);

  const lastNotifId = useRef(null);
  useEffect(() => {
      const notifId = roomData?.lastNotification?.id;
      if (notifId && notifId !== lastNotifId.current) {
          lastNotifId.current = notifId;
          setToastMessage(roomData.lastNotification.message);
          
          const timer = setTimeout(() => {
              setToastMessage("");
          }, 3000);
          
          return () => clearTimeout(timer); 
      }
  }, [roomData?.lastNotification?.id, roomData?.lastNotification?.message]);

  const prevPlayersCount = useRef(0);
  useEffect(() => {
      if (roomData?.players && view === 'lobby') {
          const currentCount = roomData.players.filter(p => !p.hasLeft).length;
          if (prevPlayersCount.current > 0 && currentCount !== prevPlayersCount.current) {
              if (currentCount > prevPlayersCount.current) playSFX('usuario_entra');
              else if (currentCount < prevPlayersCount.current) playSFX('usuario_sale');
          }
          prevPlayersCount.current = currentCount;
      }
  }, [roomData?.players, view]);

  const resetApp = () => {
    setView("hub");
    setRoomId(null);
    setRoomData(null);
    setSelectedGame(null);
    setLoading(false);
    setSettings({});
    // Mantiene la persistencia del hostPlays
    setInputPin(""); 
    prevPlayersCount.current = 0;
  };

  const handleLogout = async () => {
      await signOut(auth);
      setShowUserMenu(false);
      resetApp(); 
  };

  const upgradeUserToPremium = async (planType) => {
      if (!user) return;
      const userRef = doc(db, 'artifacts', appId, 'users', user.uid);
      
      const now = new Date();
      await setDoc(userRef, { 
          email: user.email, 
          isPremium: true,
          subscriptionType: planType || 'unknown',
          subscriptionDate: now.toISOString().split('T')[0], 
          updatedAt: Date.now()
      }, { merge: true });
      setIsPremium(true);
      setShowSubscription(false);
      alert("¡Suscripción activada con éxito! (Simulación)");
  };

  const handleManageSubscription = async () => {
      setManagingSub(true);
      try {
          const getPortal = httpsCallable(functions, 'getLemonSqueezyPortal');
          const result = await getPortal();
          
          if (result.data.url) {
              window.location.href = result.data.url;
          } else {
             throw new Error("No URL returned");
          }
      } catch (error) {
          console.error("Error al acceder al portal:", error);
          alert("Hubo un problema al abrir el gestor de suscripción. Asegúrate de haber realizado un pago antes.");
      }
      setManagingSub(false);
      setShowUserMenu(false);
  };

  const handleContinueGame = async (room) => {
      if (room.gameId === 'declarations') {
          const activePlayers = room.players.filter(p => !p.isSpectator);
          const shuffledIds = activePlayers.map(p => p.id).sort(() => Math.random() - 0.5);
          
          await updateDoc(roomsRef(room.pin), {
              status: 'playing',
              turnOrder: shuffledIds,
              currentWriterIdx: 0,
              phase: 'writing',
              currentPhrase: "",
              revealedIndices: [], 
              privateRevealed: {}, 
              winnerId: null,
              attempts: {},
              timerStart: null,
              timerDuration: 0,
              isFinalResults: false,
              roundState: 'active'
          });
          setView('game');
      } else if (room.gameId === 'russian') {
          const activePlayers = room.players.filter(p => !p.isSpectator);
          const targets = {};
          activePlayers.forEach(p => {
              const possible = activePlayers.filter(other => other.id !== p.id);
              if (possible.length > 0) {
                  const target = possible[Math.floor(Math.random() * possible.length)];
                  targets[p.id] = target.id;
              }
          });
          
          await updateDoc(roomsRef(room.pin), {
              status: 'playing',
              targets: targets,
              phase: 'write',
              r_questions: [],
              currentVoteIdx: 0,
              isFinalResults: false,
              roundState: 'active'
          });
          setView('game');
      }
  };

  const handleRestartGame = async (room) => {
      const activePlayers = room.players.filter(p => !p.isSpectator);
      const newPlayers = room.players.map(p => !p.isSpectator ? ({...p, score: 1000}) : p);
      
      let updateData = {
          status: 'playing',
          players: newPlayers,
          musicTrack: getRandomTrack(MUSIC_TRACKS[room.gameId] || MUSIC_TRACKS.lobby)
      };

      if (room.gameId === 'declarations') {
          const shuffledIds = activePlayers.map(p => p.id).sort(() => Math.random() - 0.5);
          updateData = { ...updateData,
              turnOrder: shuffledIds,
              currentWriterIdx: 0,
              phase: 'writing',
              currentPhrase: "",
              revealedIndices: [],
              privateRevealed: {},
              winnerId: null,
              attempts: {},
              timerStart: null,
              timerDuration: 0,
              isFinalResults: false,
              roundState: 'active'
          };
      } else if (room.gameId === 'russian') {
          const targets = {};
          activePlayers.forEach(p => {
              const possible = activePlayers.filter(other => other.id !== p.id);
              if (possible.length > 0) {
                  const target = possible[Math.floor(Math.random() * possible.length)];
                  targets[p.id] = target.id;
              }
          });
          updateData = { ...updateData,
              targets: targets,
              phase: 'write',
              r_questions: [],
              currentVoteIdx: 0,
              isFinalResults: false,
              roundState: 'active'
          };
      } else if (room.gameId === 'judgment') {
          const shuffledTargets = [...activePlayers].sort(() => Math.random() - 0.5);
          const rounds = shuffledTargets.map(target => ({
              targetId: target.id, 
              quality: CUALIDADES_JUICIO[Math.floor(Math.random() * CUALIDADES_JUICIO.length)]
          }));
          updateData = { ...updateData,
              judgmentRounds: rounds,
              currentJudgmentIdx: 0,
              phase: 'voting',
              j_votes: {}
          };
      }
      await updateDoc(roomsRef(room.pin), updateData);
      setView('game');
  };

  const handleLeaveRoom = async () => {
      if (!roomData || !user) return;
      const me = roomData.players.find(p => p.id === user.uid);
      
      if (!me || me.isSpectator || me.hasLeft) { 
          resetApp(); 
          return; 
      }

      const newPlayers = roomData.players.map(p => 
          p.id === user.uid ? { ...p, isSpectator: true, hasLeft: true } : p
      );

      await updateDoc(roomsRef(roomId), {
          players: newPlayers,
          lastNotification: { id: generateUUID(), message: `${me.nickname} ha abandonado la sala.`, timestamp: Date.now() }
      });
      resetApp();
  };

  const handleKickPlayers = async (uids) => {
      const kickedNames = [];
      const newPlayers = roomData.players.map(p => {
          if (uids.includes(p.id)) {
              if (!p.isSpectator && !p.hasLeft) {
                  kickedNames.push(p.nickname);
              }
              return { ...p, isSpectator: true, hasLeft: true };
          }
          return p;
      });

      if (kickedNames.length > 0) {
          await updateDoc(roomsRef(roomId), {
              players: newPlayers,
              lastNotification: { id: generateUUID(), message: `${kickedNames.join(", ")} ${kickedNames.length > 1 ? 'han' : 'ha'} abandonado la sala.`, timestamp: Date.now() }
          });
      } else {
          await updateDoc(roomsRef(roomId), { players: newPlayers });
      }
  };

  const createRoomWithPinID = async () => {
    if (user && user.isAnonymous) {
        setShowAuth(true);
        return;
    }

    if (user && !user.isAnonymous) {
        const userRef = doc(db, 'artifacts', appId, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.lastRoomCreated && (Date.now() - userData.lastRoomCreated < 30000)) {
                alert("Por favor, espera 30 segundos antes de crear otra sala.");
                return;
            }
        }
    }

    if (hostPlays && !nickname.trim()) { alert("¡Necesitas escribir tu Nombre!"); return; }
    
    const requiresTopic = ['asker', 'knowme', 'likely', 'rather'].includes(selectedGame.id);
    if (requiresTopic && !settings.topic?.trim()) {
        alert("¡Debes escribir un tema para las preguntas!");
        return;
    }
    
    if (!settings.rounds && !['impostor', 'secret', 'declarations', 'russian', 'judgment'].includes(selectedGame.id)) {
        setSettings(s => ({...s, rounds: 5}));
    }

    setLoading(true);
    const pin = generatePin();
    
    let content = [];
    const numQ = settings.rounds || 5;

    try {
        if (selectedGame.id === 'asker') {
            const prompt = `Genera ${numQ} preguntas de trivia sobre "${settings.topic || 'Cultura General'}" dificultad ${settings.difficulty || 'Media'}. Formato JSON Array: [{question: string, options: string[4], correctIndex: number}]`;
            const rawContent = await callGemini(prompt);
            
            if (!rawContent || !Array.isArray(rawContent) || rawContent.length === 0) {
                alert("La IA ha fallado generando las preguntas porque hay mucha gente jugando ahora mismo, vuelve a intentarlo");
                setLoading(false);
                return;
            }
            
            content = rawContent.map(q => {
                const uniqueOpts = [...new Set(q.options || [])].slice(0, 4);
                while(uniqueOpts.length < 4) uniqueOpts.push("Opción Extra");
                return { ...q, options: uniqueOpts };
            });
        } else if (selectedGame.id === 'knowme') {
            const prompt = `Genera ${numQ} preguntas cortas sobre gustos personales para saber si amigos se conocen. Tema "${settings.topic || 'Random'}". Usa "[Nombre]" como placeholder. RESPUESTAS TEXTUALES DESCRIPTIVAS (No uses A,B,C,D). Ejemplo: options: ["Comer Pizza", "Ir al cine", "Dormir", "Leer"]. JSON: [{question: "...", options: ["...", "...", "...", "..."]}]`;
            const rawContent = await callGemini(prompt);
            
            if (!rawContent || !Array.isArray(rawContent) || rawContent.length === 0) {
                alert("La IA ha fallado generando las preguntas porque hay mucha gente jugando ahora mismo, vuelve a intentarlo");
                setLoading(false);
                return;
            }
            content = rawContent;
        } else if (selectedGame.id === 'likely') {
            const prompt = `Genera ${numQ} frases "Quién es más probable que...". Tema: "${settings.topic || 'Random'}". JSON: [{text: "..."}]`;
            const rawContent = await callGemini(prompt);
            
            if (!rawContent || !Array.isArray(rawContent) || rawContent.length === 0) {
                alert("La IA ha fallado generando las preguntas porque hay mucha gente jugando ahora mismo, vuelve a intentarlo");
                setLoading(false);
                return;
            }
            content = rawContent;
        } else if (selectedGame.id === 'rather') {
            const prompt = `Genera ${numQ} dilemas "¿Qué prefieres?" sobre "${settings.topic || 'Random'}". JSON: [{optionA: "...", optionB: "..."}]`;
            const rawContent = await callGemini(prompt);
            
            if (!rawContent || !Array.isArray(rawContent) || rawContent.length === 0) {
                alert("La IA ha fallado generando las preguntas porque hay mucha gente jugando ahora mismo, vuelve a intentarlo");
                setLoading(false);
                return;
            }
            content = rawContent;
        } else if (['impostor', 'secret', 'declarations', 'russian', 'judgment'].includes(selectedGame.id)) {
             content = [];
        }
    } catch(e) { 
        console.error(e); 
        alert("La IA ha fallado generando las preguntas porque hay mucha gente jugando ahora mismo, vuelve a intentarlo");
        setLoading(false);
        return; 
    }

    const hostPlayer = { 
        id: user.uid, 
        nickname: hostPlays ? nickname : 'Anfitrión', 
        score: 0, 
        isHost: true, 
        isSpectator: !hostPlays, 
        avatar: hostPlays ? getRandomAvatar() : '👑',
        streak: 0,
        lastRoundPoints: 0,
        votesReceivedTotal: 0
    };

    const roomDoc = {
      pin,
      gameId: selectedGame.id,
      hostId: user.uid,
      hostIsPremium: isPremium,
      players: [hostPlayer],
      status: 'lobby',
      settings,
      content: content || [],
      currentRound: 0,
      roundState: 'waiting',
      createdAt: Date.now(),
      musicTrack: getRandomTrack(MUSIC_TRACKS.lobby)
    };
    await setDoc(roomsRef(pin), roomDoc);
    
    if (user) {
        const userRef = doc(db, 'artifacts', appId, 'users', user.uid);
        await setDoc(userRef, { lastRoomCreated: Date.now() }, { merge: true });
    }

    setRoomId(pin);
    setView('lobby');
    setLoading(false);
  };

  const joinRoom = async () => {
    if (!nickname || !inputPin) return;
    setLoading(true);
    const roomRef = roomsRef(inputPin); 
    const snap = await getDoc(roomRef);

    if (snap.exists()) {
       const data = snap.data();
       if (data.status !== 'lobby') { alert("Partida ya iniciada"); setLoading(false); return; }

       const gameConfig = GAMES.find(g => g.id === data.gameId);
       const activeCount = data.players.filter(p => !p.hasLeft).length;
       if (gameConfig && activeCount >= gameConfig.capacityMax) {
           alert("se ha alcanzado el limite de personas para este juego");
           setLoading(false);
           return;
       }

       if (data.players.some(p => p.nickname === nickname && !p.hasLeft)) {
         alert("Ese nombre ya está en uso"); setLoading(false); return;
       }

       playSFX('boton');
       await updateDoc(roomRef, {
         players: arrayUnion({ id: user.uid, nickname, score: 0, isHost: false, isSpectator: false, avatar: getRandomAvatar(), streak: 0, lastRoundPoints: 0, votesReceivedTotal: 0 })
       });
       setRoomId(inputPin);
       setSelectedGame(GAMES.find(g => g.id === data.gameId));
       setView('lobby');
    } else {
      alert("Sala no encontrada");
    }
    setLoading(false);
  };

  const startGame = async () => {
    const gameConfig = GAMES.find(g => g.id === roomData.gameId);
    
    if (roomData.players.length > 2 && !isPremium) {
        setShowSubscription(true);
        return;
    }

    let updateData = {
        status: 'playing', 
        roundState: 'active', 
        timerStart: (gameConfig?.timer > 0) ? serverTimestamp() : null,
        timerDuration: (gameConfig?.timer > 0) ? gameConfig.timer + 2 : 0,
        musicTrack: getRandomTrack(MUSIC_TRACKS[roomData.gameId] || MUSIC_TRACKS.lobby)
    };

    if (roomData.gameId === 'impostor') {
        const activePlayers = roomData.players.filter(p => !p.isSpectator);
        const randomSecret = PALABRAS_IMPOSTOR[Math.floor(Math.random() * PALABRAS_IMPOSTOR.length)];
        const shuffledIds = activePlayers.map(p => p.id).sort(() => Math.random() - 0.5);
        const randomImpostorId = shuffledIds[Math.floor(Math.random() * shuffledIds.length)];

        updateData.phase = 'input';
        updateData.roundState = 'active';
        updateData.currentRound = 1;
        updateData.impostorTurnOrder = shuffledIds;
        updateData.impostorId = randomImpostorId;
        updateData.currentSecret = { word: randomSecret.word, hint: randomSecret.hint };
        updateData.currentTurnIdx = 0;
        updateData.wordHistory = [];
        updateData.votes = {};
        updateData.totalTurnsLimit = activePlayers.length * 1;
    }
    if (roomData.gameId === 'secret') {
        updateData.phase = 'input';
        updateData.secrets = {};
        updateData.timerStart = null; 
        updateData.timerDuration = 0; 
    }
    if (roomData.gameId === 'declarations') {
        const activePlayers = roomData.players.filter(p => !p.isSpectator);
        const shuffledIds = activePlayers.map(p => p.id).sort(() => Math.random() - 0.5);
        updateData.players = roomData.players.map(p => !p.isSpectator ? ({...p, score: 1000, hasGuessed: false}) : p);
        updateData.turnOrder = shuffledIds;
        updateData.currentWriterIdx = 0;
        updateData.phase = 'writing';
        updateData.currentPhrase = "";
        updateData.revealedIndices = [];
        updateData.privateRevealed = {}; 
        updateData.winnerId = null;
        updateData.attempts = {};
        updateData.timerStart = null; 
        updateData.timerDuration = 0; 
        updateData.isFinalResults = false;
    }
    if (roomData.gameId === 'russian') {
        const activePlayers = roomData.players.filter(p => !p.isSpectator);
        const targets = {};
        const shuffled = [...activePlayers].sort(() => Math.random() - 0.5);
        for(let i=0; i<shuffled.length; i++) {
            const asker = shuffled[i];
            const target = shuffled[(i + 1) % shuffled.length];
            targets[asker.id] = target.id;
        }
        updateData.players = roomData.players.map(p => !p.isSpectator ? ({...p, score: 1000}) : p);
        updateData.targets = targets;
        updateData.phase = 'write';
        updateData.r_questions = [];
        updateData.currentVoteIdx = 0;
        updateData.timerStart = null;
        updateData.timerDuration = 0;
        updateData.isFinalResults = false;
    }
    if (roomData.gameId === 'judgment') {
        const activePlayers = roomData.players.filter(p => !p.isSpectator);
        const shuffledTargets = [...activePlayers].sort(() => Math.random() - 0.5);
        const rounds = shuffledTargets.map(target => ({
            targetId: target.id, 
            quality: CUALIDADES_JUICIO[Math.floor(Math.random() * CUALIDADES_JUICIO.length)]
        }));
        updateData.judgmentRounds = rounds;
        updateData.currentJudgmentIdx = 0;
        updateData.phase = 'voting';
        updateData.j_votes = {};
        updateData.timerStart = null;
        updateData.timerDuration = 0;
    }
    if (roomData.gameId === 'knowme') {
        const activePlayers = roomData.players.filter(p => !p.isSpectator);
        updateData.knowMeTargetId = activePlayers[0]?.id;
    }

    await updateDoc(roomsRef(roomId), updateData);
  };

  const toggleSetting = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));
  
  const renderTopicSuggestions = (topics) => (
      <details className="w-full mb-4 bg-black/40 rounded-lg overflow-hidden border border-white/10">
          <summary className="p-3 text-xs font-bold text-[rgba(255,255,255,0.6)] uppercase cursor-pointer hover:bg-white/5 select-none flex items-center justify-between">
              Sugerencias de temática <ChevronDown size={14}/>
          </summary>
          <div className="p-3 pt-0 flex flex-wrap gap-2">
            {topics.map(t => (
                <button key={t} onClick={() => toggleSetting('topic', t)} className="text-xs bg-white/10 px-2 py-1 rounded hover:bg-[rgba(0,255,255,0.2)] hover:text-[rgba(0,255,255,1)] transition text-[rgba(255,255,255,0.9)]">
                    {t}
                </button>
            ))}
          </div>
      </details>
  );

  const renderNumQuestionsSelector = (options = [5, 10, 15, 20], label = "Preguntas") => (
      <div>
         <label className="block text-sm font-bold text-[rgba(255,255,255,0.6)] mb-1">Nº de {label}</label>
         <div className="flex gap-2">
             {options.map(n => {
                 const isVipOption = n > 5;
                 const isLocked = isVipOption && !isPremium;
                 
                 return (
                 <button 
                    key={n} 
                    onClick={() => {
                        if (isLocked) {
                            if (!user || user.isAnonymous) {
                                setShowAuth(true);
                            } else {
                                setShowSubscription(true);
                            }
                            return;
                        }
                        toggleSetting('rounds', n);
                    }}
                    className={`relative flex-1 py-2 rounded border overflow-hidden transition-all ${settings.rounds === n && !isLocked ? 'bg-[rgba(0,255,255,1)] text-black border-[rgba(0,255,255,1)] shadow-[0_0_10px_rgba(0,255,255,0.5)] font-black' : 'border-white/20 text-[rgba(255,255,255,0.6)]'} ${isLocked ? 'opacity-80 hover:scale-105' : 'hover:bg-[rgba(0,255,255,0.1)]'}`}
                 >
                    {n}
                    {isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[1px]">
                            <span className="text-[10px] font-black bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-1.5 py-[2px] rounded-sm flex items-center gap-[2px] shadow-[0_0_5px_rgba(255,215,0,0.8)]">
                                <Crown size={10}/> VIP
                            </span>
                        </div>
                    )}
                 </button>
             )})}
         </div>
      </div>
  );

const getBackgroundClass = () => {
      if (view === 'game' && roomData?.gameId) {
          return 'bg-transparent';
      }
      if (view === 'results') {
          return 'bg-transparent';
      }
      return 'bg-transparent';
  };

const LogoComponent = ({ className = "" }) => (
    <div className={`px-4 py-2 ${className}`}>
        <span className="text-white font-black drop-shadow-[0_0_10px_rgba(255,255,255,0.8)] tracking-widest">ASKER GAMES</span>
    </div>
  );

  if (!user) return <div className="min-h-screen bg-[rgba(10,5,30,1)] flex items-center justify-center text-white font-mono text-xl animate-pulse">Cargando Juegos...</div>;

  return (
    <AudioContext.Provider value={{ isMuted, setIsMuted, isMusicMuted, setIsMusicMuted, playSFX }}>
    <div className={`min-h-screen ${getBackgroundClass()} font-sans text-[rgba(255,255,255,0.9)] overflow-x-hidden selection:bg-[rgba(0,255,255,0.3)] transition-colors duration-1000 relative`}>
      
      <style dangerouslySetInnerHTML={{__html: `
        .cyber-card {
            background-color: rgba(40, 70, 120, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 15px;
            box-shadow: inset 0 0 10px rgba(0,0,0,0.5), 0 10px 30px rgba(0,0,0,0.8);
            backdrop-filter: blur(12px);
            color: rgba(255, 255, 255, 0.9);
        }
        .cyber-input {
            background-color: rgba(5, 5, 15, 0.3);
            border: 2px solid rgba(0, 255, 255, 0.5);
            box-shadow: 0 0 10px rgba(0, 255, 255, 0.4);
            color: rgba(255, 255, 255, 0.9);
            outline: none;
            transition: all 0.3s ease;
        }
        .cyber-input:focus {
            border-color: rgba(0, 255, 255, 1);
            box-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
        }
        .btn-neon-cyan {
            background-color: rgba(0, 255, 255, 1);
            color: #000;
            text-transform: uppercase;
            font-weight: 900;
            letter-spacing: 0.1em;
            animation: pulse-cyan 2s infinite alternate;
        }
        @keyframes pulse-cyan {
            0% { transform: scale(1); background-color: rgba(0, 255, 255, 1); box-shadow: 0 0 10px rgba(0, 255, 255, 1); }
            100% { transform: scale(1.02); background-color: rgba(100, 255, 255, 1); box-shadow: 0 0 30px rgba(0, 255, 255, 1); }
        }
        .game-card-cyber {
            background-color: rgba(10, 5, 25, 0.8);
            border: 2px solid var(--accent);
            box-shadow: 0 0 10px var(--accent);
            transition: all 0.3s ease;
        }
        .game-card-cyber:hover {
            transform: translateY(-8px);
            background: radial-gradient(circle at center, var(--accent-alpha) 0%, rgba(20, 15, 40, 0.9) 70%);
            box-shadow: 0 0 30px var(--accent);
        }
        .game-card-cyber:hover .game-icon {
            transform: scale(1.1) rotate(5deg);
            filter: drop-shadow(0 0 15px var(--accent));
        }
        @keyframes moveRight {
            0% { transform: translateX(0); }
            100% { transform: translateX(150vw); }
        }
        @keyframes pulsate {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(1.2); opacity: 0.9; }
        }
        /* NUEVAS ANIMACIONES */
        @keyframes jelly {
            0% { transform: scale(0.1); }
            50% { transform: scale(1.1); }
            75% { transform: scale(0.9); }
            100% { transform: scale(1); }
        }
        .animate-jelly { animation: jelly 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
        
        @keyframes floatAvatar {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
        }
        .animate-float { animation: floatAvatar 3s ease-in-out infinite; display: inline-block; }

        @keyframes crownDrop {
            0% { transform: translateY(-100px) scale(2); opacity: 0; }
            60% { transform: translateY(10px) scale(0.8); opacity: 1; }
            80% { transform: translateY(-5px) scale(1.1); }
            100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        .animate-crown-drop { animation: crownDrop 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
        
        .animate-sunburst { animation: spin 15s linear infinite; }
        
        @keyframes cascadeIn {
            0% { transform: translateY(-20px) scale(0.9); opacity: 0; }
            100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        .animate-cascade { animation: cascadeIn 0.5s ease-out both; }
        
        .podium-bar { transition: height 1s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
      `}} />

      {['hub', 'pre-lobby', 'lobby'].includes(view) && <AnimatedBackground />}
      {view === 'game' && <GameBackgrounds gameId={roomData?.gameId} />}
      {view === 'results' && <ResultsBackground />}

      {toastMessage && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[200] bg-[rgba(255,20,147,0.9)] text-white pl-6 pr-2 py-2 rounded-full shadow-[0_0_20px_rgba(255,20,147,0.8)] border border-[rgba(255,20,147,1)] font-bold animate-bounce-in flex items-center gap-3 whitespace-nowrap">
              <TriangleAlert size={20} />
              <span>{toastMessage}</span>
              <button onClick={() => setToastMessage("")} className="p-1 hover:bg-[rgba(255,20,147,1)] rounded-full transition-colors ml-2">
                  <X size={18} className="text-white"/>
              </button>
          </div>
      )}

      {/* HEADER PRINCIPAL */}
      <div className="p-4 flex justify-between items-start z-50 relative">
        {view !== 'hub' ? (
            <button onClick={resetApp} className="text-xl font-black italic hover:scale-105 transition-transform cursor-pointer mt-2">
                <LogoComponent />
            </button>
        ) : <div className="w-10 h-10" />}
        
        <div className="flex items-center gap-4">
            <div className="flex flex-col items-end gap-2 relative mt-2">
                {user && !user.isAnonymous ? (
                    <>
                        <button onClick={() => setShowUserMenu(!showUserMenu)} className="cyber-card flex items-center gap-2 px-4 py-2 !rounded-full hover:bg-white/10 transition-all">
                            {user.photoURL ? <img src={user.photoURL} className="w-6 h-6 rounded-full"/> : <User size={18} className="text-[rgba(0,255,255,1)]"/>}
                            <span className="text-xs font-bold hidden md:inline text-white">{user.email?.split('@')[0]}</span>
                            {isPremium && <Crown size={14} className="text-[rgba(255,215,0,1)] drop-shadow-[0_0_5px_rgba(255,215,0,0.8)]"/>}
                        </button>
                        
                        {showUserMenu && (
                            <div className="absolute top-12 right-0 w-48 cyber-card overflow-hidden z-[60] animate-fade-in !p-0">
                                {isPremium ? (
                                    <button onClick={handleManageSubscription} disabled={managingSub} className="w-full text-left px-4 py-3 hover:bg-[rgba(0,255,255,0.1)] flex items-center gap-2 text-sm text-[rgba(0,255,255,1)]">
                                        {managingSub ? <Loader size={16} className="animate-spin" /> : <CreditCard size={16}/>} 
                                        {managingSub ? "Cargando..." : "Gestionar suscripción"}
                                    </button>
                                ) : (
                                    <button onClick={() => { setShowSubscription(true); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-[rgba(255,20,147,0.2)] flex items-center gap-2 text-sm text-[rgba(255,20,147,1)]">
                                        <Crown size={16}/> Suscripción
                                    </button>
                                )}
                                <div className="h-px bg-white/10"/>
                                <button onClick={handleLogout} className="w-full text-left px-4 py-3 hover:bg-[rgba(255,20,147,0.2)] flex items-center gap-2 text-sm text-[rgba(255,20,147,1)]">
                                    <LogOut size={16}/> Cerrar Sesión
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    view === 'hub' && (
                        <Button onClick={() => setShowAuth(true)} variant="outline" className="text-xs py-1 px-4 !rounded-full !w-auto">
                            <User size={14} className="mr-2 text-[rgba(0,255,255,1)]"/> Entrar
                        </Button>
                    )
                )}

                <div className="flex gap-2">
{/* BOTÓN MUTE MÚSICA */}
                    <button
                        onClick={() => setIsMusicMuted(!isMusicMuted)}
                        className={`cyber-card px-3 py-2 !rounded-full flex items-center justify-center transition-all ${isMusicMuted ? '!bg-red-600 !border-red-400' : 'hover:bg-white/10'}`}
                        title={isMusicMuted ? "Activar Música" : "Mutear Música"}
                    >
                        <Music size={16} className="text-white"/>
                    </button>

{/* BOTÓN MUTE SFX */}
                    <button
                        onClick={() => setIsMuted(!isMuted)}
                        className={`cyber-card px-3 py-2 !rounded-full flex items-center justify-center transition-all ${isMuted ? '!bg-red-600 !border-red-400' : 'hover:bg-white/10'}`}
                        title={isMuted ? "Activar SFX" : "Mutear SFX"}
                    >
                        {isMuted ? <VolumeX size={16} className="text-white"/> : <Volume2 size={16} className="text-white"/>}
                    </button>
                </div>
            </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-20 relative z-10 mt-[-20px]">
        
        {view === 'hub' && (
          <div className="animate-fade-in-up relative">
            
            <div className="text-center py-4 mb-8">
              <h1 className="text-5xl md:text-8xl font-black italic tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-fuchsia-500 to-white drop-shadow-[0_0_20px_rgba(192,38,211,0.6)] mb-2">
                ASKER GAMES
              </h1>
              <p className="text-lg md:text-xl text-[rgba(255,255,255,0.6)] font-bold tracking-widest uppercase drop-shadow-md">
                Los mejores juegos para grupos de amigos
              </p>
            </div>

            <div className="mb-12 flex justify-center">
                <div className="cyber-card p-8 max-w-md w-full relative z-10">
                  <h3 className="text-xl font-black mb-6 text-center text-[rgba(0,255,255,1)] uppercase tracking-widest" style={{textShadow: '0 0 10px rgba(0,255,255,0.5)'}}>Entra en una sala</h3>
                  <div className="flex gap-2 mb-4">
                    <Input 
                      value={inputPin} 
                      onChange={(e) => setInputPin(e.target.value)} 
                      placeholder="PIN" 
                      maxLength={4}
                      className="text-center tracking-[0.5em] font-mono text-xl uppercase font-bold"
                    />
                  </div>
                  <div>
                    <Input 
                        value={nickname} 
                        onChange={(e) => setNickname(e.target.value)} 
                        placeholder="Tu Nombre" 
                        maxLength={15}
                        className="mb-4 text-center font-bold"
                    />
                    <Button onClick={joinRoom} variant="primary" disabled={loading}>
                      {loading ? "Conectando..." : "UNIRSE A PARTIDA"}
                    </Button>
                  </div>
                </div>
            </div>

            <div className="flex justify-center mb-12">
                <button 
                    onClick={() => setShowBugReport(true)}
                    className="text-[rgba(255,20,147,1)] hover:text-white text-sm flex items-center gap-2 px-6 py-2 border border-[rgba(255,20,147,0.5)] rounded-full hover:bg-[rgba(255,20,147,0.2)] hover:shadow-[0_0_15px_rgba(255,20,147,0.5)] transition-all font-bold tracking-widest uppercase"
                >
                    <Bug size={14} /> Comunícanos errores o sugerencias aquí
                </button>
            </div>

            <h2 className="text-3xl md:text-4xl font-black text-center mb-10 text-white uppercase tracking-widest" style={{textShadow: '0 0 20px rgba(0,255,255,0.8)'}}>
              Crea una sala
            </h2>

<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {GAMES.map(g => (
                <GameCard key={g.id} game={g} onClick={(game) => {
                  setSelectedGame(game);
                  const savedMusicMode = localStorage.getItem('asker_music_mode') || 'host';
                  setSettings({ 
                      rounds: 5, 
                      difficulty: 'Media', 
                      topic: '',
                      anonymousVotes: true,
                      musicMode: savedMusicMode,
                      useHints: true
                  });
                  setView('pre-lobby');
                }} />
              ))}
            </div>

            {/* FOOTER */}
            <footer className="mt-20 py-8 border-t border-[rgba(255,255,255,0.1)] text-center text-[rgba(255,255,255,0.4)] text-sm font-mono">
                <div className="flex flex-col md:flex-row justify-center items-center gap-4 mb-4">
                    <span>Contactoaskergames@gmail.com</span>
                    <span className="hidden md:inline text-[rgba(0,255,255,0.5)]">•</span>
                    <span>© 2026 Asker Games. Todos los derechos reservados.</span>
                </div>
                <div className="flex flex-wrap justify-center gap-4 text-xs tracking-widest uppercase">
                    <a href="aviso-legal.html" rel="nofollow" target="_blank" className="hover:text-[rgba(0,255,255,1)] transition-colors">Aviso Legal</a>
                    <a href="politica-privacidad.html" rel="nofollow" target="_blank" className="hover:text-[rgba(0,255,255,1)] transition-colors">Privacidad</a>
                    <a href="politica-cookies.html" rel="nofollow" target="_blank" className="hover:text-[rgba(0,255,255,1)] transition-colors">Cookies</a>
                    <a href="terminos-condiciones.html" rel="nofollow" target="_blank" className="hover:text-[rgba(0,255,255,1)] transition-colors">Términos</a>
                </div>
            </footer>
          </div>
        )}
        
        {view === 'pre-lobby' && selectedGame && (
          <div className="max-w-md mx-auto mt-10 animate-fade-in">
             <Button onClick={resetApp} variant="secondary" className="mb-6 !py-2 !w-auto"><ArrowLeft size={16}/> Volver</Button>
             <Card>
               <div className="text-center mb-6">
                 <div className="text-5xl mb-2 text-[rgba(0,255,255,1)] drop-shadow-[0_0_15px_rgba(0,255,255,0.8)]">{selectedGame.icon}</div>
                 <h2 className="text-3xl font-black uppercase italic tracking-widest text-white" style={{textShadow: '0 0 10px rgba(255,255,255,0.5)'}}>{selectedGame.title}</h2>
                 <p className="text-[rgba(255,255,255,0.6)] mt-2">{selectedGame.description}</p>
               </div>
               
               <div className="space-y-4">
                 <div className="flex items-center justify-between bg-black/40 border border-[rgba(255,255,255,0.1)] p-3 rounded-xl">
                    <span className="text-sm font-bold flex items-center gap-2 text-[rgba(255,255,255,0.8)]"><Monitor size={16} className="text-[rgba(0,255,255,1)]"/> JUGAR EN TV (Anfitrión no juega)</span>
                    <button 
                        onClick={() => {
                            const newVal = !hostPlays;
                            setHostPlays(newVal);
                            localStorage.setItem('asker_host_plays', newVal.toString());
                        }}
                        className={`w-12 h-6 rounded-full transition-colors relative shadow-inner ${!hostPlays ? 'bg-[rgba(50,205,50,0.8)] shadow-[0_0_10px_rgba(50,205,50,0.5)]' : 'bg-gray-800'}`}
                    >
                        <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${!hostPlays ? 'left-7' : 'left-1'}`} />
                    </button>
                 </div>

{/* RADIO BUTTONS MUSICA */}
                 <div className="flex flex-col bg-black/40 border border-[rgba(255,255,255,0.1)] p-4 rounded-xl mb-4 gap-3 shadow-inner">
                    <span className="text-sm font-bold flex items-center gap-2 text-[rgba(255,255,255,0.8)]"><Music size={16} className="text-[rgba(255,20,147,1)]"/> Quién escucha la música:</span>
                    <div className="flex gap-2">
                        {['host', 'all', 'none'].map(mode => {
                            const currentMode = settings.musicMode || 'host';
                            return (
                                <button 
                                    key={mode}
                                    onClick={() => {
                                        toggleSetting('musicMode', mode);
                                        localStorage.setItem('asker_music_mode', mode);
                                    }}
                                    className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase transition-all ${currentMode === mode ? 'bg-[rgba(255,20,147,1)] text-white shadow-[0_0_10px_rgba(255,20,147,0.5)]' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                                >
                                    {mode === 'host' ? 'Solo Anfitrión' : mode === 'all' ? 'Todos' : 'Nadie'}
                                </button>
                            );
                        })}
                    </div>
                 </div>

                 {selectedGame.id === 'impostor' && (
                   <div className="flex items-center justify-between bg-black/40 border border-[rgba(255,255,255,0.1)] p-3 rounded-xl mb-4">
                      <span className="text-sm font-bold flex items-center gap-2 text-[rgba(255,255,255,0.8)]"><Search size={16} className="text-blue-400"/> Usar Pistas</span>
                      <button 
                          onClick={() => toggleSetting('useHints', !settings.useHints)}
                          className={`w-12 h-6 rounded-full transition-colors relative shadow-inner ${settings.useHints ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]' : 'bg-gray-800'}`}
                      >
                          <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${settings.useHints ? 'left-7' : 'left-1'}`} />
                      </button>
                   </div>
                 )}

                 {selectedGame.id === 'judgment' && (
                   <div className="flex items-center justify-between bg-black/40 border border-[rgba(255,255,255,0.1)] p-3 rounded-xl mb-4">
                      <span className="text-sm font-bold flex items-center gap-2 text-[rgba(255,255,255,0.8)]"><EyeOff size={16} className="text-blue-400"/> Votos Anónimos</span>
                      <button 
                          onClick={() => toggleSetting('anonymousVotes', !settings.anonymousVotes)}
                          className={`w-12 h-6 rounded-full transition-colors relative shadow-inner ${settings.anonymousVotes ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]' : 'bg-gray-800'}`}
                      >
                          <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${settings.anonymousVotes ? 'left-7' : 'left-1'}`} />
                      </button>
                   </div>
                 )}

                 {hostPlays && (
                    <div>
                        <label className="block text-sm font-bold text-[rgba(255,255,255,0.6)] mb-1 uppercase tracking-widest">Tu Nombre</label>
                        <Input value={nickname} onChange={e => setNickname(e.target.value)} placeholder="Ej. Juan" maxLength={15} />
                    </div>
                 )}
                 
                 {/* SETTINGS POR JUEGO */}
                 {selectedGame.id === 'asker' && (
                   <>
                    <div>
                      <label className="block text-sm font-bold text-[rgba(255,255,255,0.6)] mb-1 uppercase tracking-widest">Tema (Requerido)</label>
                      <Input value={settings.topic || ''} onChange={e => toggleSetting('topic', e.target.value)} placeholder="Cultura, Música, Historia..." maxLength={40} className="mb-2"/>
                      {renderTopicSuggestions(selectedGame.topics || ["Cultura general", "Noticias de actualidad", "Historia", "Música", "Deportes", "Biblia", "Geografía", "Ciencia", "Literatura", "Política", "Cine", "Videojuegos", "Celebridades", "Tecnología"])}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[rgba(255,255,255,0.6)] mb-1 uppercase tracking-widest">Dificultad</label>
                      <div className="flex gap-2">
                        {['Fácil', 'Media', 'Difícil', 'Extrema'].map(d => (
                            <button key={d} onClick={() => toggleSetting('difficulty', d)} className={`flex-1 py-2 rounded border transition-all ${settings.difficulty === d ? 'bg-[rgba(0,255,255,1)] text-black border-[rgba(0,255,255,1)] shadow-[0_0_10px_rgba(0,255,255,0.5)] font-black' : 'border-white/20 text-[rgba(255,255,255,0.6)] hover:bg-[rgba(0,255,255,0.1)]'}`}>{d}</button>
                        ))}
                      </div>
                    </div>
                    {renderNumQuestionsSelector()}
                   </>
                 )}

                 {selectedGame.id === 'knowme' && (
                   <>
                    <div>
                      <label className="block text-sm font-bold text-[rgba(255,255,255,0.6)] mb-1 uppercase tracking-widest">Tema (Requerido)</label>
                      <Input value={settings.topic || ''} onChange={e => toggleSetting('topic', e.target.value)} placeholder="Ej. Random, Comida, Viajes..." maxLength={40} className="mb-2"/>
                      {renderTopicSuggestions(selectedGame.topics || ["Random", "Entretenimiento", "Comida", "Preferencias amorosas", "Amistad", "Regalos", "Viajes", "Actividades"])}
                    </div>
                    {renderNumQuestionsSelector()}
                   </>
                 )}

                 {['likely', 'rather'].includes(selectedGame.id) && (
                   <>
                    <div>
                      <label className="block text-sm font-bold text-[rgba(255,255,255,0.6)] mb-1 uppercase tracking-widest">Tema (Requerido)</label>
                      <Input value={settings.topic || ''} onChange={e => toggleSetting('topic', e.target.value)} placeholder="Ej. Random..." maxLength={40} className="mb-2"/>
                      {renderTopicSuggestions(selectedGame.topics || ["Random", "Amistad", "Situaciones vergonzosas", "Liarla parda"])}
                    </div>
                    {renderNumQuestionsSelector()}
                   </>
                 )}

                 <Button onClick={createRoomWithPinID} variant="primary" className="mt-8" disabled={loading}>
                   {loading ? "Creando..." : "CREAR SALA"}
                 </Button>
               </div>
             </Card>
          </div>
        )}

        {view === 'lobby' && roomData && (
          <div className="max-w-2xl mx-auto mt-10 animate-fade-in text-center">
             <div className="flex justify-between items-center mb-8">
                 <Button onClick={handleLeaveRoom} variant="danger" className="!w-auto !py-2 !px-4 text-xs !shadow-none !border-none !bg-red-600 !text-white"><LogOut size={16} className="mr-1"/> Salir</Button>
                 {roomData.hostId === user.uid && (
                     <Button onClick={() => setKickModalOpen(true)} variant="danger" className="!w-auto !py-2 !px-4 text-xs !bg-red-600 !text-white !shadow-none !border-none"><UserMinus size={16} className="mr-1"/> Expulsar</Button>
                 )}
             </div>

             <div className="text-6xl mb-4 text-[rgba(0,255,255,1)] drop-shadow-[0_0_15px_rgba(0,255,255,0.8)] flex justify-center">{selectedGame?.icon}</div>
             <h2 className="text-4xl font-black uppercase italic tracking-widest text-white mb-8 flex items-center justify-center gap-3" style={{textShadow: '0 0 10px rgba(255,255,255,0.5)'}}>
                 {selectedGame?.title}
                 <button onClick={() => setShowGameInfo(true)} className="text-white hover:text-white transition-colors mt-1" title="Cómo jugar">
                     <Info size={24} />
                 </button>
             </h2>
             
             <div className="cyber-card p-6 md:p-10 mb-8 inline-block shadow-[0_0_30px_rgba(0,255,255,0.3)] border-[rgba(0,255,255,0.5)]">
                 <p className="text-sm font-bold text-[rgba(255,255,255,0.6)] uppercase tracking-widest mb-2">PIN DE LA SALA</p>
                 <div className="text-6xl md:text-8xl font-black text-[rgba(0,255,255,1)] tracking-widest" style={{textShadow: '0 0 20px rgba(0,255,255,0.8)'}}>{roomId}</div>
             </div>

             <h3 className="text-xl font-bold mb-4 uppercase tracking-widest flex items-center justify-center gap-2">
                 <Users className="text-[rgba(255,20,147,1)]"/> Jugadores ({roomData.players.filter(p => !p.hasLeft).length} / {selectedGame?.capacityMax || '?'})
             </h3>
             
<div className="flex flex-wrap justify-center gap-4 mb-12 min-h-[100px]">
                 {roomData.players.filter(p => !p.hasLeft).map((p, idx) => (
                     <div key={p.id} className="bg-black/40 border border-[rgba(255,255,255,0.1)] px-4 py-3 rounded-xl flex items-center gap-3 animate-jelly shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                         <span className="text-2xl animate-float" style={{ animationDelay: `${(idx % 5) * 0.2}s` }}>{p.avatar}</span>
                         <span className="font-bold text-white">{p.nickname}</span>
                         {p.isHost && <Crown size={16} className="text-[rgba(255,215,0,1)] drop-shadow-[0_0_5px_rgba(255,215,0,0.8)] ml-1"/>}
                         {p.isSpectator && !p.isHost && <Eye size={16} className="text-[rgba(255,255,255,0.4)] ml-1"/>}
                     </div>
                 ))}
             </div>

             {roomData.hostId === user.uid ? (
                 <Button 
                    onClick={startGame} 
                    disabled={roomData.players.filter(p => !p.isSpectator && !p.hasLeft).length < 1}
                    className="w-full max-w-md mx-auto text-xl py-4"
                 >
                     ¡EMPEZAR PARTIDA!
                 </Button>
             ) : (
                 <div className="text-[rgba(255,255,255,0.6)] font-bold animate-pulse text-lg tracking-widest uppercase">
                     Esperando al anfitrión...
                 </div>
             )}
          </div>
        )}

        {view === 'game' && roomData && (
          <GameEngine 
            room={roomData} 
            user={user} 
            roomRef={roomsRef(roomId)} 
            gameId={roomData.gameId} 
            onShowInfo={() => setShowGameInfo(true)}
            onOpenKick={() => setKickModalOpen(true)}
            onLeaveRoom={handleLeaveRoom}
          />
        )}

        {view === 'results' && roomData && (
          <div className="mt-10">
             <GlobalRanking 
                room={roomData}
                roomRef={roomsRef(roomId)}
                players={roomData.players} 
                isFinal={true} 
                onNext={resetApp} 
                gameId={roomData.gameId}
                onContinue={() => handleContinueGame(roomData)}
                onRestart={() => handleRestartGame(roomData)}
                isHost={roomData.hostId === user.uid}
                roomStatus={roomData.status}
             />
          </div>
        )}

      </div>

      {/* MODALES */}
      {kickModalOpen && <KickModal room={roomData} onClose={() => setKickModalOpen(false)} onKick={handleKickPlayers} />}
      {showBugReport && <BugReportModal onClose={() => setShowBugReport(false)} user={user} appId={appId} />}
      {showGameInfo && selectedGame && <GameInfoModal game={selectedGame} onClose={() => setShowGameInfo(false)} />}
      
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onLoginSuccess={() => setShowAuth(false)} />}
      
      {showSubscription && (
          <SubscriptionModal 
            onClose={(result) => { 
                if(result && result.success) upgradeUserToPremium(result.plan);
                else setShowSubscription(false);
            }} 
          />
      )}

    </div>
    </AudioContext.Provider>
  );
}